import {
    colorHex,
    iconSymbol,
    normalizeColorId,
    normalizeIconId,
    PLAYER_COLORS,
    PLAYER_ICONS,
} from "../data/avatars.js";
import {createCardLibrary} from "../data/cards.js";
import {createCardSoundBoard} from "../audio/soundboard.js";
import {CARD_POWER, chooseBotMove} from "./botPolicy.js";
import {cardMoves, cardNoticeFor, freshHandFlags, tableSnapshot} from "./cardMoves.js";
import {playConsequences} from "./cardShow.js";
import {createCombat} from "./combat.js";
import {addStat, matchAwards, ordinal, placementOf, statRows} from "./matchStats.js";
import {
    BLACKJACK_PRIZE,
    DONE_MS,
    MAX_ROUNDS,
    MINIGAME_TITLES,
    VOTE_PRIZE,
    blackjackHand,
    blackjackLeaders,
    blackjackStatusText,
    botHits,
    stackOf,
    botTarget,
    cardFace,
    dealStacks,
    normalizeMinigame,
    stepComplete,
    stepDuration,
    voteTally,
    voteTickets,
    worstHand,
} from "./miniGames.js";
import {
    bottleRing,
    bottleSpin,
    coinFrame,
    diceFace,
    dicePips,
    duelFaces,
    gambleOutcome,
    gamblePhase,
    normalizeGamble,
    rouletteFrame,
    wheelFrame,
    wheelPointer,
} from "./gambleShow.js";
import {DECK_CATEGORIES, addCard, deckBuilderView, draftChanged, removeCard, startDraft, togglePick, unpickAt} from "./deckBuilder.js";
import {fitOpeningHand, isValidDeck, normalizeSettings, standardDeck, startingCards} from "./deckRules.js";
import {Player, PlayerManager} from "./player.js";
import {arenaHeight, boardLayoutFor, seatFit, seatPosition, seatScale} from "./boardLayout.js";
import {isPhoneMode, isTeamMode, normalizeMode, seatsFor, GAME_MODES, PARTY_MODE} from "./modes.js";
import {
    nextTeam,
    normalizeTeam,
    opponentsOf,
    teamLabel,
    teamMatchOutcome,
    teammatesOf,
    turnDraws,
    TEAM_LABELS,
    TEAM_ROSTER,
} from "./teams.js";
import {advanceActive, matchOutcome, withoutPlayer} from "./turnOrder.js";
import {drinkAlertSource, personalDrinkEvent, recentDrinkEvents} from "./drinkEvents.js";
import {buildBoardHistory, formatDrinkLine} from "./eventLog.js";
import {attackableEnemies, canPlayAnyCard, defaultAdjustedCost as adjustedCost, notePlayed, payForCard, turnContinuationMessage} from "./turnRules.js";
import {HOST_GRACE_MS, secondsLeft, turnClockLabel, turnDeadline} from "./turnClock.js";
import {resolveGameView} from "./viewRules.js";
import {
    AccountClient,
    accountName,
    accountPanel,
    ANONYMOUS,
    leaderboardRows,
    loginUrl,
    preferredName,
    recentGameRows,
} from "../utils/account.js";
import {makeId, normalizeCode, normalizeName, randomInt} from "../utils/identity.js";
import {numberText} from "../utils/sanitize.js";
import {GameStorage} from "../utils/storage.js";

// One shared cap: trimming card and drink events to different lengths used to evict a
// card event while its drinks survived, orphaning them in the board history. Minion
// events share the window too, which is why it is wider than the original 20.
const MAX_EVENTS = 30;
// Bots are a dev switch (?bots=1) for playing the game alone, not a party feature.
// Long enough that the board reads like someone taking their turn.
const BOT_TURN_DELAY = 1500;
const MAX_BOT_DELAY = 10000;
const MAX_BOT_ROSTER = 6;
const BOT_NAMES = ["Bot Klara", "Bot Rune", "Bot Sixten", "Bot Vera", "Bot Mange", "Bot Doris"];
const POLL_INTERVAL = 1200;
// How often the turn clock looks at its deadline. A second is as fine as it is shown.
const TURN_TICK = 1000;
// The last five seconds of a turn are painted as urgent.
const TURN_URGENT_SECONDS = 5;
// How long a phone opening a share link waits for /auth/me before joining as a guest.
// Long enough for a signed-in deck and name to arrive; short enough not to hold anyone up.
const ACCOUNT_WAIT_MS = 2500;
// How long the leave button stays armed after the first tap. Long enough to reach it
// again deliberately, short enough that it is never still waiting when a later tap
// lands somewhere near it.
const LEAVE_CONFIRM_MS = 4000;
// How long a device keeps walking back into the lobby it remembers. It mirrors the
// stores' 12h maxAgeMs: past that the server has dropped the game and handed the
// five-letter code to whoever asks for it next, so resuming would be a stranger's party.
const SESSION_MEMORY_MS = 12 * 60 * 60 * 1000;
// How long the phone keeps telling a player which cards a play moved in or out of their
// hand. Longer than the drink alert: a theft lands while the victim is looking at the TV.
const CARD_NOTICE_MS = 12000;
// The award mats the board lays out under the winner: two rows of four fit a laptop.
const BOARD_AWARDS = 8;
// How old a chance card's play may be and still get its moment on a phone that has only
// just seen it. Generous, because phone clocks disagree and polls arrive late.
const GAMBLE_FRESH_MS = 10000;

// Everything the old startApp() closure did except touch the DOM. The React shell
// subscribes to snapshots and calls the actions; every side effect that unit tests
// cannot host (URL bar, redirects, clipboard, timers) arrives injected so the whole
// controller runs under node's test runner with plain stubs.
export function createGameController({
    storage = new GameStorage(),
    accountClient = new AccountClient(),
    soundBoard = createCardSoundBoard(),
    search = globalThis.location?.search ?? "",
    pathname = globalThis.location?.pathname ?? "/",
    origin = globalThis.location?.origin ?? "",
    replaceUrl = (url) => globalThis.history.replaceState(null, "", url),
    redirect = (url) => {
        globalThis.location.href = url;
    },
    clipboard = globalThis.navigator?.clipboard,
    now = Date.now,
    scheduleTimeout = globalThis.setTimeout.bind(globalThis),
    scheduleInterval = globalThis.setInterval.bind(globalThis),
} = {}) {
    const combat = createCombat({makeId, recordDrinkEvent, recordMinionEvent, recordHeal, recordBlock, drawCard, pickRebound});
    const {
        addSips,
        attackPlayer,
        drainNarration,
        hitMinions,
        removeSips,
        summonMinion,
        triggerTurnEndMinions,
        triggerTurnStartDrinks,
        triggerTurnStartMinions,
    } = combat;
    const CARD_LIBRARY = createCardLibrary({
        addSips,
        attackPlayer,
        discardCard: (player) => Player.discardCard(player, randomInt),
        drawCard,
        hitMinions,
        millCards: (player, count) => Player.millCards(player, count),
        randomInt: cardRandomInt,
        removeSips,
        stealCard: (from, to) => Player.stealCard(from, to, randomInt),
        summonMinion,
        swapHands: (first, second) => Player.swapHands(first, second),
        startMinigame,
    });
    // What a player may build with: everything, the teamwork cards included. A built deck
    // is played in every mode, so outside a 2 mot 2 those only do their solo half — the
    // player's call, made in the builder's Lag tab.
    const BUILDABLE_CARDS = CARD_LIBRARY;
    const BUILDABLE_IDS = BUILDABLE_CARDS.map((card) => card.id);
    const qs = new URLSearchParams(search);
    let view = qs.get("view") || "board";
    const urlGameCode = normalizeCode(qs.get("game") || "");
    const playerId = storage.getPlayerId(makeId);
    const botsEnabled = qs.get("bots") === "1";
    // ?botdelay=0 runs a match as fast as the browser can render it, which is how you
    // check a rule change without sitting through 1.5s a card.
    const requestedBotDelay = Number(qs.get("botdelay"));
    const botDelay = qs.has("botdelay") && Number.isFinite(requestedBotDelay)
        ? Math.min(Math.max(requestedBotDelay, 0), MAX_BOT_DELAY)
        : BOT_TURN_DELAY;

    let state = null;
    let account = ANONYMOUS;
    let lobbyMessage = null;
    const playerManager = new PlayerManager(() => state, playerId);
    let selectedCardIndex = null;
    // Whether the expanded card is waiting for the player to pick an opponent. The
    // rest of the old three-state selectionMode was derivable from selectedCardIndex.
    let targeting = false;
    // Card names this device's player has played in the current turn — a purely
    // local UI aid for the phone's "Spelat den här turen" chips, never synced.
    // Replaced rather than mutated so every snapshot can share the array.
    let playedThisTurn = [];
    // Read once rather than on every snapshot: the lobby form only uses it to seed an
    // uncontrolled input, and joinGame is the one place the stored name changes.
    let savedName = storage.getPlayerName();
    // The look this device plays under. Seeded off the player id so a device that
    // never opens the picker still shows up as somebody, and normalized here rather
    // than in storage so the allow-list has exactly one home on the way in and out.
    const storedAppearance = storage.getAppearance();
    let chosenIcon = normalizeIconId(storedAppearance.icon, playerId);
    let chosenColor = normalizeColorId(storedAppearance.color, playerId);
    // With no TV the table strip and the hand share one small screen, so the strip
    // folds away. Remembered per device, because it is a preference about this
    // phone's screen and not something the other players should see change.
    let tableOpen = storage.getTableOpen();
    // The lobby asks one question at a time: create or join, and only then the things
    // that answer needs — a mode for the creator, a code for the joiner. A device that
    // arrived on a share link has already answered the first question.
    let lobbyStep = urlGameCode ? "join" : "start";
    // The board shows the log permanently; a phone has no room for it, so every mode
    // gets the same history behind one button instead.
    let historyOpen = false;
    // Leaving a started match is one-way: joinGame refuses a seat once the match is
    // running, so a stray tap would end someone's evening with no way back. The first
    // tap arms, the second leaves.
    let leaveArmed = false;
    // The drink alert this phone's player tapped away. Only ever the newest one: a fresh
    // hit has a fresh id, so it shows again without anything having to reset this.
    let dismissedAlertId = null;
    // Same rule for the card notice: only the newest one is ever dismissed.
    let dismissedNoticeId = null;
    // The all-time board as the server sent it, plus the lobby code it was fetched
    // for, which is what keeps it to one fetch per lobby.
    let leaderboard = [];
    let leaderboardCode = null;
    let botTimer = null;
    // Players a card's own damage landed on while it resolved — set by playCard, read
    // by the drink and minion recorders, so a burst can follow where sips really went.
    let landedIds = null;
    // Why the sips being recorded right now are drunk, when no attacker can say: set
    // around the fatigue sip, so its event and its statistic both know it was the deck.
    let drinkCause = null;
    // The lobby's deckbuilder: the draft being edited (null while closed) and how its
    // last save went. Local to this device until saved to the account.
    let deckDraft = null;
    let deckSaveStatus = null;
    // The draft as it was opened or last saved: closing with anything else in hand takes
    // two taps, because a stray tap on Stäng would throw away a half-built deck.
    let deckDraftBase = null;
    let deckCloseArmed = false;
    // Which of the builder's tabs is open, and whether it shows only what the deck holds.
    let deckTab = DECK_CATEGORIES[0].id;
    let deckOnlyInDeck = false;
    // The host's two switches. Kept here rather than in the synced state until the
    // game starts: a whole-state POST in the lobby would race the phones' joins.
    let gameSettings = normalizeSettings();
    // The seat the host screen has armed for a kick: the first tap on "Sparka" arms it,
    // the second on the same seat kicks, so a stray click on the TV throws nobody out.
    let kickPendingId = null;
    // The turn clock: which turn this device last saw act, and when it saw it. Keyed on
    // the state's turnStamp plus whose turn it is, so a play or a new turn restarts it.
    let turnSeen = null;
    // Set while the clock is asking the server for news before it skips anyone, so a
    // second tick cannot skip the same turn twice.
    let turnClockBusy = false;
    // The rolls a chance card makes while it resolves — collected only inside playCard,
    // so a mirror's or a thief's own randomness never ends up in a card's show.
    let cardRolls = null;
    // Chance plays this device has seen, and the local moment it first saw each: a
    // phone's reveal runs off that, not off the play's timestamp from another clock.
    const gambleSeen = new Map();
    let dismissedRevealId = null;
    // The mini-game step this device last saw, and when: its countdown runs off that
    // local moment, like the turn clock, because the phones' clocks do not agree.
    let minigameSeen = null;
    // Set while the settling device asks the server for the last answers, so a second
    // tick cannot settle the same step twice.
    let minigameBusy = false;
    // The bot answers this host has already sent for a step, so a slow merge is not sent
    // again on the next tick.
    const botAnswered = new Set();
    // A finished mini-game this phone has tapped away, so its result stops covering the hand.
    let dismissedMinigameId = null;

    const listeners = new Set();
    const effectListeners = new Set();
    let snapshot = null;

    function buildSnapshot() {
        return {
            game: state,
            view,
            account,
            lobbyMessage,
            selectedCardIndex,
            playedThisTurn,
            playerId,
            urlGameCode,
            // Derived, not stored: savedName stays the mirror of what this device has
            // in localStorage, and the account only fills it in when that is empty. So
            // signing out reverts the field for free, and joinGame stays its one writer.
            savedName: preferredName(account, savedName),
            appearance: {icon: chosenIcon, color: chosenColor},
            tableOpen,
            lobbyStep,
            historyOpen,
            leaveArmed,
        };
    }

    function emitChange() {
        snapshot = buildSnapshot();
        listeners.forEach((listener) => listener());
    }

    function emitEffect(effect) {
        effectListeners.forEach((listener) => listener(effect));
    }

    function save() {
        // saveGame tolerates a missing game itself, so no guard is needed here.
        storage.saveGame(state);
    }

    function loadLocal(code) {
        return storage.loadLocal(code);
    }

    async function loadGame(code) {
        const remote = await storage.fetchRemote(code);
        if (remote) return normalizeGameState(remote);
        return normalizeGameState(loadLocal(code));
    }

    function makeCode() {
        const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
        let code = "";
        for (let i = 0; i < 5; i += 1) code += alphabet[randomInt(0, alphabet.length - 1)];
        return code;
    }

    // The library has grown past the deck everyone used to share; the standard deck is
    // frozen in deckRules so guests and bots keep exactly the mix they always had. A
    // table with sides adds the teamwork cards, which need somebody to pass a card to.
    function buildDeck() {
        return shuffle(standardDeck({teams: isTeamGame()}));
    }

    function shuffle(items) {
        const copy = [...items];
        for (let i = copy.length - 1; i > 0; i -= 1) {
            const j = randomInt(0, i);
            [copy[i], copy[j]] = [copy[j], copy[i]];
        }
        return copy;
    }

    function cardById(id) {
        return CARD_LIBRARY.find((card) => card.id === id);
    }

    function playerById(id) {
        return state.players.find((player) => player.id === id) || null;
    }

    function sipsText(amount) {
        return `${amount} klunk${amount === 1 ? "" : "ar"}`;
    }

    // Party mode makes a TV lobby the creating device only watches. A 2 mot 2 has no
    // TV: the phone that creates it takes the first seat and still owns the lobby, so
    // it is the one device that may start the match and drive the bots.
    function createGame({mode, name} = {}) {
        const chosenMode = normalizeMode(mode);
        const seated = isPhoneMode({mode: chosenMode});
        // A phone-mode creator takes a seat, so the same rule applies as joining.
        // A party lobby is only a screen and needs no name at all.
        if (seated && !seatName(name)) return setLobbyMessage("Skriv ditt namn och välj figur först.");
        let code = makeCode();
        while (loadLocal(code)) code = makeCode();
        state = {
            code,
            status: "lobby",
            round: 1,
            active: 0,
            hostId: playerId,
            // Party is stored as no field at all, so a blob written before modes
            // existed and one written now are the same thing.
            mode: chosenMode === PARTY_MODE ? undefined : chosenMode,
            log: seated
                ? `${modeLabel(chosenMode)} är skapat. Väntar på ${seatsFor(chosenMode) - 1} telefon${seatsFor(chosenMode) === 2 ? "" : "er"} till.`
                : "TV-lobbyn är skapad. Väntar på telefoner.",
            events: [],
            players: [],
        };
        if (seated) state.players.push(seatForThisDevice(name, nextTeam(state.players)));
        save();
        pushUrl(code, seated ? "hand" : "board");
        refresh();
    }

    // Who this device would be seated as: the account name first, then whatever was
    // typed, then what the device remembers. Empty means nobody has said yet, which
    // is a different thing from a default and is what the callers check.
    function seatName(requestedName) {
        return normalizeName(accountName(account) || (requestedName || "").trim() || savedName.trim());
    }

    // The seat this device plays under, name rules and all. joinGame and the 2 mot 2
    // creator both need it, and the name rule must not exist in two places.
    function seatForThisDevice(requestedName, team) {
        // Both callers refuse to seat a device seatName() cannot answer for, so there
        // is no nameless case left to default here.
        const name = seatName(requestedName);
        storage.setPlayerName(name);
        savedName = name;
        return Player.create({
            name,
            id: playerId,
            deck: buildDeck(),
            icon: chosenIcon,
            color: chosenColor,
            team,
            ...broughtDeck(),
        });
    }

    async function openGame(code) {
        const normalized = normalizeCode(code);
        if (!normalized) return;
        state = await loadGame(normalized);
        if (!state) return setLobbyMessage("Ingen lobby hittades med den koden.");
        normalizeGameState(state);
        if (state.status === "closed") return exitToLobby("Lobbyn är stängd.");
        pushResolvedUrl(normalized);
        refresh();
    }

    async function joinGame(code, requestedName) {
        const normalized = normalizeCode(code);
        if (!normalized) return setLobbyMessage("Skriv in en kod först.");
        state = await loadGame(normalized);
        if (!state) return setLobbyMessage("Ingen lobby hittades med den koden.");
        normalizeGameState(state);
        const existingPlayer = state.players.some((player) => player.id === playerId);
        if (state.status === "closed") return exitToLobby("Lobbyn är stängd.");
        // A signed-in player typing the code on a new phone already has a seat here: take
        // it over rather than being turned away from a running match, or seated twice.
        const seat = account.liveGame;
        if (!existingPlayer && seat?.code === normalized && state.players.some((player) => player.id === seat.playerId)) {
            return reconnect();
        }
        if (state.status !== "lobby" && !existingPlayer) {
            return exitToLobby("Spelet har redan startat. Nya spelare kan inte gå med.");
        }
        // A 2 mot 2 is exactly four seats. Past that there is no side to put someone
        // on, so the fifth phone is told rather than silently seated as a spectator.
        const seats = seatsFor(state.mode);
        if (seats && !existingPlayer && state.players.length >= seats) {
            return exitToLobby(`Matchen är full. ${modeLabel(state.mode)} rymmer ${seats} spelare.`);
        }
        // Only a host *without* a seat is a TV. In a 2 mot 2 the owner is one of the
        // four players, so this branch has to look at the seat rather than the role.
        if (!existingPlayer && state.hostId === playerId) {
            state.log = "Den här enheten hostar skärmen och är inte en spelare.";
            save();
            pushUrl(normalized, "board");
            refresh();
            return;
        }

        // A scanned share link calls this straight from boot(). A phone that has never
        // played would be seated as "Spelare" without ever seeing the form — and the
        // form is the only place it can pick a figure and a colour — so a first seat
        // needs a name first. Rejoining is exempt: that seat already has one.
        if (!existingPlayer && !seatName(requestedName)) {
            state = null;
            return setLobbyMessage("Skriv ditt namn och välj figur för att gå med.");
        }

        // A signed-in player is their account name. The lobby drops its name field
        // once you sign in, so there is nothing typed to honour — and the seat then
        // carries the same name on every device the account plays from.
        if (!existingPlayer) {
            const player = seatForThisDevice(requestedName, nextTeam(state.players));
            const name = player.name;
            const log = `${name} gick med.`;
            // Everyone scans the code at once. A whole-state POST would overwrite the
            // other phones' joins, so the server merges this one player in instead.
            const merged = await storage.joinRemote(normalized, player, log);
            if (merged && merged.code === normalized) {
                state = normalizeGameState(merged);
                storage.saveLocal(state);
            } else {
                state.players.push(player);
                state.log = log;
                save();
            }
        }
        pushResolvedUrl(normalized);
        refresh();
    }

    // A signed-in player's saved deck travels in their seat, because the host deals and
    // the host cannot see anyone's account. It is locked in here: rejoining keeps the
    // seat as it was. The host checks it again before dealing — any device can POST.
    function broughtDeck() {
        const deck = account.deck;
        if (!deck || !isValidDeck(deck.cards, BUILDABLE_IDS)) return {};
        return {customDeck: deck.cards, openingHand: fitOpeningHand(deck.openingHand, deck.cards)};
    }

    // Picking is two one-line actions rather than a form field because the lobby paints
    // swatches: the choice has to survive a re-render, and component state is the one
    // place this project does not put state.
    function chooseIcon(id) {
        if (!PLAYER_ICONS.some((icon) => icon.id === id)) return;
        chosenIcon = id;
        storage.setAppearance(chosenIcon, chosenColor);
        emitChange();
    }

    function goToCreate() {
        setLobbyStep("create");
    }

    function goToJoin() {
        setLobbyStep("join");
    }

    // One step back, which from either branch is the first question again.
    function goBack() {
        setLobbyStep("start");
    }

    function setLobbyStep(step) {
        lobbyStep = step;
        lobbyMessage = null;
        emitChange();
    }

    // One tap from a player who is still in it only arms the button; a player who is
    // already out has nothing left to lose and goes straight away.
    function requestLeave() {
        if (leaveArmed || !canLoseSomethingByLeaving()) {
            leaveGame();
            return;
        }
        leaveArmed = true;
        scheduleTimeout(disarmLeave, LEAVE_CONFIRM_MS);
        emitChange();
    }

    function disarmLeave() {
        if (!leaveArmed) return;
        leaveArmed = false;
        emitChange();
    }

    function canLoseSomethingByLeaving() {
        const me = myPlayer();
        return Boolean(state?.status === "playing" && me && !isDead(me));
    }

    function leaveLabel() {
        return leaveArmed ? "Säker?" : "Lämna";
    }

    function toggleHistory() {
        historyOpen = !historyOpen;
        emitChange();
    }

    function toggleTable() {
        tableOpen = !tableOpen;
        storage.setTableOpen(tableOpen);
        emitChange();
    }

    function chooseColor(id) {
        if (!PLAYER_COLORS.some((color) => color.id === id)) return;
        chosenColor = id;
        storage.setAppearance(chosenIcon, chosenColor);
        emitChange();
    }

    function pushResolvedUrl(code) {
        pushUrl(code, resolveGameView({
            requestedView: view,
            isHost: isHostDevice(),
            hasPlayer: Boolean(myPlayer()),
        }));
    }

    function pushUrl(code, targetView) {
        view = targetView;
        // Every path that puts this device into a game ends here, which makes it the
        // one place the lobby has to be remembered for the next visit.
        storage.rememberSession(code, now());
        const params = new URLSearchParams();
        params.set("game", code);
        if (targetView !== "duo") params.set("view", targetView);
        // The bot switches are read once at boot, so they have to survive every URL
        // rewrite or a reload mid-simulation lands on a lobby with no way to add bots.
        // The delay is written back sanitised rather than echoed.
        if (botsEnabled) {
            params.set("bots", "1");
            params.set("botdelay", String(botDelay));
        }
        replaceUrl(`${pathname}?${params.toString()}`);
    }

    // Both attack lists are game rules, so a synced state only keeps string ids.
    function normalizeIdList(value) {
        return Array.isArray(value) ? value.filter((id) => typeof id === "string") : [];
    }

    function normalizeGameState(game) {
        if (!game) return null;
        if (!Array.isArray(game.players)) game.players = [];
        if (!Array.isArray(game.events)) game.events = [];
        // Which rules the match runs under is the most load-bearing field in the blob,
        // so it is allow-listed to the one alternative mode rather than trusted.
        const mode = normalizeMode(game.mode);
        game.mode = mode === PARTY_MODE ? undefined : mode;
        game.turnAttackedIds = normalizeIdList(game.turnAttackedIds);
        game.protectedIds = normalizeIdList(game.protectedIds);
        game.kickedIds = normalizeIdList(game.kickedIds);
        // The winner drives the celebration screen, so a forged id must not survive.
        if (typeof game.winnerId !== "string") game.winnerId = null;
        // What restarts the turn clock: a fresh id on every turn start and every play.
        if (typeof game.turnStamp !== "string") game.turnStamp = null;
        // A mini-game is painted on every screen at the table, so it is coerced like a seat.
        game.minigame = normalizeMinigame(game.minigame);
        game.settings = normalizeSettings(game.settings);
        const before = game.players.length;
        // The host is a TV and never a player — except in a 2 mot 2, which has no TV:
        // there the owner is one of the four and filtering them out would empty a side.
        const keepHost = isPhoneMode(game);
        game.players = game.players
            .filter((player) => keepHost || player.id !== game.hostId)
            .map((player) => Player.normalize(player));
        PlayerManager.normalizeActiveIndex(game);
        if (state === game && before !== game.players.length) save();
        return game;
    }

    function setLobbyMessage(message) {
        lobbyMessage = message;
        emitChange();
    }

    // Resolves as soon as the account is known. Linking carries on behind it: nothing
    // waits on the link, and a share-link join should not wait on it either.
    async function refreshAccount() {
        account = await accountClient.load();
        emitChange();
        // The device's player id is stable in localStorage, so re-linking on every
        // load is what attributes games played before the player signed in.
        if (account.authenticated) accountClient.link(playerId);
    }

    // Called from refresh(), which is the only place that knows which surface a device
    // actually paints — the boot-time ?view is a request, not an answer, and the
    // controller rewrites it. Stamping the code before the await keeps concurrent
    // refreshes to one fetch, and makes a new lobby pick up the evening's results.
    async function refreshLeaderboard(code) {
        leaderboardCode = code;
        leaderboard = await accountClient.leaderboard();
        emitChange();
    }

    function startLogin() {
        redirect(loginUrl(`${pathname}${search}`));
    }

    async function endLogin() {
        account = await accountClient.logout();
        emitChange();
    }

    function exitToLobby(message) {
        lobbyStep = urlGameCode ? "join" : "start";
        storage.forgetSession();
        state = null;
        selectedCardIndex = null;
        targeting = false;
        replaceUrl(pathname);
        lobbyMessage = message;
        emitChange();
    }

    function closeLobbyFromHost() {
        state.status = "closed";
        state.players = [];
        state.events = [];
        state.log = "Lobbyn stängdes av skärmen.";
        save();
        exitToLobby("Lobbyn stängdes. Alla spelare har skickats ut.");
    }

    // pagehide fires on a reload and on every tab the browser discards, so closing a
    // lobby that phones are already sitting in would end the party on a stray swipe —
    // and the screen walks back into it on the next visit anyway. An empty lobby has
    // nobody to come back for, so that one is still cleaned up on the way out.
    function closeLobbyOnPageExit() {
        // Optional chaining because a synced blob is only guaranteed to have a player
        // list once normalizeGameState has seen it, and pagehide can land before that.
        if (!state || state.status !== "lobby" || state.players?.length || !isHostDevice() || !storage.canSync()) return;
        const closedGame = {
            ...state,
            status: "closed",
            players: [],
            events: [],
            log: "Lobbyn stängdes av skärmen.",
        };
        storage.saveLocal(closedGame);
        storage.sendBeaconGame(closedGame);
    }

    function startGame() {
        if (!state || !isHostDevice()) return;
        if (state.players.length < 2) {
            state.log = "Vänta in minst två telefoner innan spelet startar.";
            save();
            refresh();
            return;
        }
        state.status = "playing";
        state.round = 1;
        state.active = 0;
        state.turnStamp = makeId();
        state.turnAttackedIds = [];
        state.protectedIds = [];
        state.settings = {...gameSettings};
        state.players.forEach((player) => {
            const {deck, openingHand} = startingCards({player, settings: state.settings, knownIds: BUILDABLE_IDS, shuffle, teams: isTeamGame()});
            Player.resetForGame(player, deck, Player.drawCard, openingHand);
        });
        state.log = `Spelet har startat. ${dealSummary(state.settings)}`;
        selectedCardIndex = null;
        save();
        refresh();
    }

    function dealSummary(settings) {
        if (settings.deckMode === "shared") return "Alla spelar standardleken.";
        return settings.openingHand === "fixed" ? "Egna lekar, egen starthand." : "Egna lekar, slumpad starthand.";
    }

    function setDeckMode(mode) {
        gameSettings = normalizeSettings({...gameSettings, deckMode: mode});
        emitChange();
    }

    function setOpeningHandMode(mode) {
        gameSettings = normalizeSettings({...gameSettings, openingHand: mode});
        emitChange();
    }

    function openDeckBuilder() {
        if (!account.authenticated) return;
        deckDraft = startDraft(account.deck, BUILDABLE_IDS);
        deckDraftBase = deckDraft;
        deckSaveStatus = null;
        deckCloseArmed = false;
        deckTab = DECK_CATEGORIES[0].id;
        deckOnlyInDeck = false;
        emitChange();
    }

    function closeDeckBuilder() {
        if (deckDraft && !deckCloseArmed && draftChanged(deckDraft, deckDraftBase)) {
            deckCloseArmed = true;
            scheduleTimeout(disarmDeckClose, LEAVE_CONFIRM_MS);
            emitChange();
            return;
        }
        deckDraft = null;
        deckDraftBase = null;
        deckSaveStatus = null;
        deckCloseArmed = false;
        emitChange();
    }

    function disarmDeckClose() {
        if (!deckCloseArmed) return;
        deckCloseArmed = false;
        emitChange();
    }

    function editDeckDraft(edit) {
        if (!deckDraft) return;
        deckDraft = edit(deckDraft);
        deckSaveStatus = null;
        deckCloseArmed = false;
        emitChange();
    }

    function chooseDeckTab(id) {
        if (!DECK_CATEGORIES.some((category) => category.id === id)) return;
        deckTab = id;
        emitChange();
    }

    function toggleDeckFilter() {
        deckOnlyInDeck = !deckOnlyInDeck;
        emitChange();
    }

    async function saveDeck() {
        if (!deckBuilder()?.canSave) return;
        deckSaveStatus = "saving";
        emitChange();
        const posted = deckDraft;
        const saved = await accountClient.saveDeck(posted);
        if (saved) {
            account = {...account, deck: saved};
            deckDraftBase = posted;
        }
        deckSaveStatus = saved ? "saved" : "failed";
        emitChange();
    }

    function myPlayer() {
        return playerManager.mine();
    }

    function isHostDevice() {
        return playerManager.isHostDevice();
    }

    function currentPlayer() {
        return playerManager.current();
    }

    function enemiesOf(player) {
        return playerManager.enemiesOf(player);
    }

    function livingAlliesOf(player) {
        if (!isTeamGame()) return [];
        return teammatesOf(state.players, player).filter((mate) => !isDead(mate));
    }

    // Every draw a card or a minion makes, counted for the end screen. The opening hand
    // and the turn's own draw call Player.drawCard directly: everyone gets those, so
    // counting them would only drown the difference.
    function cardRandomInt(min, max) {
        const roll = randomInt(min, max);
        if (cardRolls) cardRolls.push(roll);
        return roll;
    }

    function drawCard(player) {
        const drew = Player.drawCard(player);
        if (drew) addStat(player, "drawn");
        return drew;
    }

    // Tvåvägsspegeln: a random one of the target's other enemies, never the attacker. In a
    // 2 mot 2 that is the attacker's partner; with nobody left combat sends it back.
    function pickRebound(target, source) {
        const others = enemiesOf(target).filter((player) => player.id !== source.id);
        return others.length ? others[randomInt(0, others.length - 1)] : null;
    }

    // Where a Fyllekompis may wander: the planter's enemies. A planter who has left
    // takes nobody's side, so it stays where it is.
    function drifterHosts(minion) {
        const planter = playerById(minion.placedBy);
        return planter ? enemiesOf(planter) : [];
    }

    function isDead(player) {
        return Player.isDead(player);
    }

    // Only combat calls this, always with all four arguments and always mid-play, so
    // normalizeGameState has already guaranteed state.events is an array.
    function recordDrinkEvent(player, amount, source, lethal) {
        noteLanded(player, source);
        countDrink(player, amount, source, lethal);
        state.events.push({
            id: makeId(),
            type: lethal ? "death" : "drink",
            playerId: player.id,
            playerName: player.name,
            // Fatigue drinks have no source: exhaustion is nobody's attack.
            sourceId: source?.id || null,
            sourceName: source?.name || null,
            amount,
            ...(drinkCause ? {cause: drinkCause} : {}),
            at: now(),
        });
        state.events = state.events.slice(-MAX_EVENTS);
    }

    // Same contract as recordDrinkEvent: only combat calls this, always mid-play.
    // targetName doubles as the minion's name so the stats tables can keep a generic
    // column; killed is browser-only detail for the board history line.
    function recordMinionEvent(owner, minion, amount, killed, source) {
        noteLanded(owner, source);
        // What a guard soaked is what it saved its owner from drinking.
        addStat(owner, "blocked", amount);
        if (killed && source && source.id !== owner.id) addStat(source, "minionKills");
        state.events.push({
            id: makeId(),
            type: "minion",
            playerId: owner.id,
            playerName: owner.name,
            minionName: minion.name,
            targetName: minion.name,
            // The board keeps a fallen guard standing until the card that felled it has
            // slid there, and by then it is off the board: this is what it is drawn from.
            minionId: minion.id,
            minionImage: minion.image || null,
            minionMaxHp: minion.maxHp,
            // Every current attack supplies a source; combat's signature allows
            // none, so the fallbacks wait for a sourceless hit to exist.
            /* c8 ignore next */
            sourceId: source?.id || null,
            /* c8 ignore next */
            sourceName: source?.name || null,
            amount,
            killed,
            at: now(),
        });
        state.events = state.events.slice(-MAX_EVENTS);
    }

    // The end screen's drinking numbers. A sip you send yourself (Skål!, a cactus you
    // poked) is its own statistic rather than one you dealt; fatigue has nobody to credit.
    function countDrink(player, amount, source, lethal) {
        addStat(player, "drunk", amount);
        if (drinkCause === "fatigue") addStat(player, "fatigue", amount);
        if (!source) return;
        if (source.id === player.id) {
            addStat(player, "selfSips", amount);
            return;
        }
        addStat(source, "dealt", amount);
        if (lethal) addStat(source, "knockouts");
    }

    // Combat reports what a heal really took off and what an umbrella, a coaster or a
    // two-way mirror really stopped.
    function recordHeal(player, amount) {
        addStat(player, "healed", amount);
    }

    function recordBlock(player, amount) {
        addStat(player, "blocked", amount);
    }

    // Only the card's own hits count. A cactus prick or a mirror bounce has somebody
    // else as its source, and is not where the card landed.
    function noteLanded(player, source) {
        if (landedIds && source?.id === landedIds.sourceId && !landedIds.ids.includes(player.id)) landedIds.ids.push(player.id);
    }

    function needsTarget(card) {
        return !card || card.target === "enemy";
    }

    // The old render(): everything it decided about state stays here, everything it
    // painted moved into the React components that read the snapshot.
    function refresh() {
        if (!state && urlGameCode) state = loadLocal(urlGameCode);
        if (state?.players) normalizeGameState(state);
        if (state?.status === "closed") return exitToLobby("Lobbyn är stängd.");
        if (state) {
            const resolvedView = resolveGameView({
                requestedView: view,
                isHost: isHostDevice(),
                hasPlayer: Boolean(myPlayer()),
            });
            if (resolvedView !== view) pushUrl(state.code, resolvedView);
            // The waiting room is the only surface that paints the board, so this is
            // where it is worth fetching: a phone that typed the code never asks, and
            // a host that reopens its own share link still gets one.
            if (resolvedView === "board" && state.status === "lobby" && leaderboardCode !== state.code) {
                refreshLeaderboard(state.code);
            }
        }
        observeTurn();
        observeGambles();
        observeMinigame();
        // The turn can also move away over sync (a host skip, a leaver reshuffling
        // seats): once it is no longer this player's turn, "this turn" is over.
        if (playedThisTurn.length && !isMyTurn()) playedThisTurn = [];
        // Every path that can hand the turn to a bot ends in a refresh: starting the
        // game, a player ending their turn, a poll landing a newer state, and the bot's
        // own moves. Hooking the driver here means none of them has to remember to.
        scheduleBotTurn();
        emitChange();
    }

    // Reached only from playCard, after normalizeGameState made state.events an array.
    // Answers the event: its id is what the minions the play summons carry as their
    // playId, and a chance card adds its gamble to it once the rolls are in.
    function recordCardEvent(player, card, target, text = card.text) {
        const event = {
            id: makeId(),
            type: "card",
            playerId: player.id,
            playerName: player.name,
            cardId: card.id,
            cardName: card.name,
            cardText: text,
            cardImage: card.image,
            cardEffect: card.effect,
            cardTag: card.tag,
            // The board slides the card to a seat, and two players may share a name.
            targetId: target?.id || null,
            targetName: target?.name || null,
            at: now(),
        };
        state.events.push(event);
        state.events = state.events.slice(-MAX_EVENTS);
        return event;
    }

    // One event per play, however many cards it moved: a Stolleken at a full table must
    // not push ten other plays out of the shared event window.
    function recordCardMoves(self, card, table) {
        // A card this bundle does not know (a newer deploy, a forged hand) moves unnamed.
        const moves = cardMoves(table, state.players, (id) => cardById(id)?.name || "okänt kort");
        if (!moves.length) return;
        moves.forEach((move) => {
            if (move.kind === "take" && move.toId === self.id) addStat(self, "stolen", move.cardIds.length);
        });
        state.events.push({
            id: makeId(),
            type: "cards",
            playerId: self.id,
            playerName: self.name,
            cardId: card.id,
            cardName: card.name,
            moves,
            at: now(),
        });
        state.events = state.events.slice(-MAX_EVENTS);
    }

    function selectCard(index) {
        if (!state) return;
        const me = myPlayer();
        if (!me) return;
        selectedCardIndex = index;
        const card = cardById(me.hand[selectedCardIndex]);
        targeting = false;
        if (state.status === "playing" && currentPlayer()?.id === me.id) {
            state.log = needsTarget(card)
                ? "Tryck Spela och välj ett mål på telefonen."
                : "Tryck Spela för att använda kortet.";
        }
        refresh();
    }

    function cancelSelection() {
        selectedCardIndex = null;
        targeting = false;
        refresh();
    }

    function closeTargetModal() {
        targeting = false;
        refresh();
    }

    function requestPlaySelectedCard() {
        const card = selectedCard();
        if (!card) return;
        if (needsTarget(card)) {
            targeting = true;
            state.log = "Välj motståndare på telefonen.";
            refresh();
            return;
        }
        playSelectedCard();
    }

    function isMyTurn() {
        const me = myPlayer();
        return Boolean(me && state?.status === "playing" && currentPlayer()?.id === me.id);
    }

    // The one place a card is actually resolved. Both the phone and the bot driver go
    // through it, so a bot can never play by rules a player does not have.
    // Answers false for a card the player cannot afford, otherwise the ids of the
    // players its own damage landed on, in order (empty when it landed nowhere).
    function playCard(self, card, target, handIndex) {
        // A finished mini-game has had its moment; the next card clears it off the table.
        if (state.minigame?.phase === "done") state.minigame = null;
        const manaBefore = self.mana;
        if (!payForCard(self, card, adjustedCost)) return false;
        addStat(self, "played");
        addStat(self, "manaSpent", manaBefore - self.mana);
        if (target && target.id !== self.id) addStat(target, "targeted");
        const play = recordCardEvent(self, card, target);
        const playId = play.id;
        // Every play restarts the turn clock, on every device that sees it.
        state.turnStamp = playId;
        soundBoard.playCard(card);
        drainNarration();
        // The card leaves the hand BEFORE its own effect runs. A card is not in your
        // hand while you are playing it, and Player.drawCard refuses to go past six:
        // with a full hand, Citronpress, Virgin Colada and Isvatten were counting
        // themselves against that cap, so their draw was silently refused and the card
        // left anyway — playing "dra ett kort" put you one card down. Bytt är bytt
        // swaps the hand and Konfettikanonen counts it, so they rely on this too.
        self.hand.splice(handIndex, 1);
        // The table as the card finds it, so whatever it draws, takes, swaps or throws
        // away can be named afterwards — on the phones it touched, and on the board.
        const table = tableSnapshot(state.players);
        landedIds = {sourceId: self.id, ids: []};
        const standing = new Set(state.players.flatMap((player) => player.minions.map((minion) => minion.id)));
        // Living teammates only: a card that heals or shields a partner who is
        // already out would be spent on nobody, and the cards read the list's length
        // to decide whether their team half happens at all.
        const enemies = enemiesOf(self);
        cardRolls = [];
        card.play({self, target, enemies, allies: livingAlliesOf(self)});
        // A chance card's rolls, read back into what they meant: the coin's face, the
        // die, who the roulette picked. Every screen builds its suspense from this.
        const gamble = gambleOutcome(card, {rolls: cardRolls, self, target, enemies});
        cardRolls = null;
        if (gamble) play.gamble = gamble;
        // A minion this play put on the bar — on any seat: Snyltgästen and Lagkaptenen
        // land on somebody else's — remembers the card, for its art and for the board's
        // show, which keeps it off the bar until the card has slid to it.
        state.players.forEach((player) => player.minions.forEach((minion) => {
            if (standing.has(minion.id)) return;
            Object.assign(minion, {image: card.image, playId});
            addStat(self, "summoned");
        }));
        recordCardMoves(self, card, table);
        notePlayed(self, card, target);
        const landed = landedIds.ids;
        landedIds = null;
        // Aimed damage marks the victim for next turn's protection rule. Area cards
        // stay unmarked — they cannot be aimed, so they cannot be dodged either.
        if (card.effect === "damage" && card.target === "enemy" && target && !state.turnAttackedIds.includes(target.id)) {
            state.turnAttackedIds.push(target.id);
        }
        // No replacement draw: the turn-start draw in advanceTurn and the cards that
        // draw by their own text are the only ways a hand refills.
        state.log = turnContinuationMessage(self, card.name, cardById, adjustedCost, drainNarration());
        if (state.minigame?.phase === "play" && state.minigame.round === 1 && !Object.keys(state.minigame.answers).length) {
            state.log = `${self.name} spelar ${card.name}! ${state.minigame.kind === "vote" ? "Alla röstar" : "Alla spelar"} på telefonen.`;
        }
        return landed;
    }

    function playSelectedCard(targetIndex) {
        const self = myPlayer();
        if (!self || minigameOpen()) return;
        // The play button is hidden off-turn, but the target modal can outlive a synced
        // turn change, so the rule is enforced here rather than by the UI alone.
        if (!isMyTurn()) return;
        const card = selectedCard();
        const target = Number.isInteger(targetIndex) ? state.players[targetIndex] : null;
        if (!card) return;
        if (needsTarget(card) && (!target || target.id === self.id || isDead(target))) return;
        // The picker already hides protected targets; this catches a modal that
        // outlived a synced turn change, same as the isDead guard above.
        if (needsTarget(card) && !attackableEnemies(state, enemiesOf(self)).some((enemy) => enemy.id === target.id)) return;

        const landed = playCard(self, card, target, selectedCardIndex);
        if (!landed) {
            state.log = `${card.name} kostar ${adjustedCost(self, card)} mana. Du har ${self.mana}.`;
            refresh();
            return;
        }
        selectedCardIndex = null;
        targeting = false;
        playedThisTurn = [...playedThisTurn, card.name];
        burstForCard(card, targetIndex, landed);
        save();
        refresh();
        if (maybeEndGame()) return;
        // Pricked, popped or toasted out on your own turn: the death screen covers
        // Avsluta tur, so the turn has to move on by itself.
        if (isDead(self)) advanceTurn();
    }

    // outgoingActs is false when the host kicks the player whose turn it is: the turn
    // moves on, but a leaver's minions do not get a parting shot.
    function advanceTurn({outgoingActs = true, note = ""} = {}) {
        // Both callers (endTurn, the bot driver) have already checked for a playing
        // state; an empty roster can still arrive over sync and must not crash.
        if (!state.players.length) return;
        // A turn never carries a mini-game into the next one.
        state.minigame = null;
        // Pinger minions throw at the end of their owner's turn, before the rotation,
        // so a Kapsylkastare summoned this turn already gets its throw. Only the
        // device that advances the turn rolls the target (single-writer, like the bot
        // driver), and the pings neither mark protection targets nor respect the
        // protection list — a thrown bottle cap cannot be dodged.
        const outgoing = currentPlayer();
        drainNarration();
        // A host can force a turn forward off a seat that died mid-rotation; a dead
        // owner's minions stay quiet.
        if (outgoingActs && !isDead(outgoing)) {
            triggerTurnEndMinions({owner: outgoing, enemies: enemiesOf(outgoing), allies: livingAlliesOf(outgoing), randomInt});
        }
        // Hämnden counts from here: what you drink between this turn and your next.
        outgoing.recentSips = 0;
        const pings = drainNarration();
        const pingDetail = pings.length ? ` ${pings.join(" ")}` : "";
        // The outgoing player's victims are protected from the incoming player only.
        state.protectedIds = state.turnAttackedIds;
        state.turnAttackedIds = [];
        const {active, round} = advanceActive(state, isDead);
        state.active = active;
        state.round = round;
        state.turnStamp = makeId();
        const current = currentPlayer();
        current.maxMana = Math.min(10, state.round);
        current.mana = Math.max(0, current.maxMana - (current.manaPenalty || 0));
        current.manaPenalty = 0;
        current.playedCount = 0;
        // An emptied deck starts hurting from the turn AFTER the one that drew it
        // dry, so exhaustion is read before the turn-start draw.
        const exhausted = current.deck.length === 0;
        // The turn-start draw. The last player on a side of a 2 mot 2 draws two — see
        // turnDraws — and only the cards that actually landed are announced, so an
        // empty deck or a full hand does not claim a card nobody got.
        let extras = 0;
        // Promenaden: the walk costs this turn's draw, extra draws and all.
        const draws = current.skipDraw ? 0 : turnDraws(state, current, isDead);
        current.skipDraw = false;
        for (let draw = 0; draw < draws; draw += 1) {
            const drew = Player.drawCard(current);
            if (draw > 0 && drew) extras += 1;
        }
        const soloDetail = extras
            ? ` ${current.name} är ensam kvar i laget och drar ${extras} extra kort.`
            : "";
        let fatigueDetail = "";
        if (exhausted) {
            current.fatigue += 1;
            // Sourceless on purpose: exhaustion is nobody's attack, so it neither
            // triggers reflect nor lets the player's own minions soak it. The cause is
            // what lets every screen say it was the empty deck rather than a mystery.
            drinkCause = "fatigue";
            addSips(current, current.fatigue);
            drinkCause = null;
            fatigueDetail = ` ${current.name} är utan kort och dricker ${sipsText(current.fatigue)} i utmattning.`;
        }
        const startDetail = turnStartSips(current);
        selectedCardIndex = null;
        targeting = false;
        playedThisTurn = [];
        const lead = note ? `${note} ` : "";
        state.log = `${lead}${current.name}s tur. ${current.mana}/${current.maxMana} mana.${pingDetail}${soloDetail}${fatigueDetail}${startDetail}`;
        save();
        refresh();
        // A ping or the fatigue sip can eliminate a player — the turn change must be
        // allowed to end the match, and a freshly dead incoming player must not be
        // left holding a turn they can never end.
        if (maybeEndGame()) return;
        if (isDead(current)) advanceTurn();
    }

    // What lands as a turn begins: the minions first (every Snyltgäst and Fyllekompis,
    // Kassören's mana), then Bakfyllan's hangover and a sip of any Långdrink. The drinks
    // go straight past the player's minions and are credited to whoever set them up —
    // so a mirror can bounce them back.
    function turnStartSips(player) {
        triggerTurnStartMinions({owner: player, findPlayer: playerById, hostsFor: drifterHosts, randomInt});
        triggerTurnStartDrinks({owner: player, findPlayer: playerById});
        const lines = drainNarration();
        return lines.length ? ` ${lines.join(" ")}` : "";
    }

    // Restarts the clock whenever the turn's key changes — a new turn, a play, or the
    // seat holding the turn changing under a kick or a leaver.
    function observeTurn() {
        const current = state?.status === "playing" ? currentPlayer() : null;
        const key = current ? `${state.turnStamp || `${state.round}:${state.active}`}|${current.id}` : null;
        if (key === turnSeen?.key) return;
        turnSeen = key ? {key, at: now()} : null;
    }

    // The countdown everyone sees, or null when no human's turn is running. Bots are
    // played by the host's bot driver, which never dawdles.
    function turnClock() {
        // A mini-game has a clock of its own; the turn's waits for it.
        if (state?.status !== "playing" || !turnSeen || minigameOpen()) return null;
        const current = currentPlayer();
        if (current.isBot || isDead(current)) return null;
        const stuck = !canPlayAnyCard(current, cardById, adjustedCost);
        const deadline = turnDeadline(turnSeen.at, stuck);
        const seconds = secondsLeft(deadline, now());
        const mine = current.id === playerId;
        return {
            deadline,
            stuck,
            seconds,
            mine,
            urgent: seconds <= TURN_URGENT_SECONDS,
            label: turnClockLabel({seconds, stuck, mine, name: current.name}),
            short: `${seconds} s`,
        };
    }

    // Once a second. The player's own phone ends its own turn on time; the host screen is
    // the backup for a phone that went quiet and waits HOST_GRACE_MS longer. Either way
    // it asks the server first: a play it has not polled yet restarts the count, and
    // skipping on a stale state would overwrite that play.
    async function tickTurnClock() {
        const clock = turnClock();
        if (!clock || turnClockBusy) return;
        if (!clock.mine && !isHostDevice()) return;
        if (now() < clock.deadline + (clock.mine ? 0 : HOST_GRACE_MS)) return;
        const key = turnSeen.key;
        turnClockBusy = true;
        await pollOnce();
        turnClockBusy = false;
        const fresh = turnClock();
        if (!fresh || turnSeen.key !== key) return;
        timeOutTurn(fresh.stuck);
    }

    // ── Mini-games ─────────────────────────────────────────────────────────────
    // A mini-game card hands the turn to a game every phone at the table plays at once.
    // Phones answer through the server's merge; one device — the starter's own phone, or
    // the host as the backup, like the turn clock — settles each step with a whole-state
    // save once every answer is in or the step's time is up, asking the server first.

    function minigameOpen() {
        return Boolean(state?.minigame && state.minigame.phase !== "done");
    }

    // Called by the card library from inside playCard: every living seat plays.
    function startMinigame(kind, starter) {
        const seats = state.players.filter((player) => !isDead(player)).map((player) => player.id);
        state.minigame = {
            id: makeId(),
            gameId: makeId(),
            kind,
            phase: "play",
            round: 1,
            tie: 0,
            starterId: starter.id,
            seats,
            stacks: kind === "blackjack" ? dealStacks(seats, shuffle) : {},
            answers: {},
            hands: null,
            winnerId: null,
            result: null,
        };
    }

    function observeMinigame() {
        const step = state?.minigame?.id || null;
        if (step === minigameSeen?.id) return;
        minigameSeen = step ? {id: step, at: now()} : null;
    }

    // Observed on the way in, so a screen asking before the next refresh still counts
    // from the moment this device first saw the step.
    function minigameElapsed() {
        observeMinigame();
        return now() - minigameSeen.at;
    }

    function livingIds() {
        return state.players.filter((player) => !isDead(player)).map((player) => player.id);
    }

    // A seat whose player has left reads as nobody in particular.
    function nameOf(id) {
        return playerById(id)?.name ?? "?";
    }

    // "Alice och Bob", "Alice, Bob och Cleo".
    const LIST_FORMAT = new Intl.ListFormat("sv", {type: "conjunction"});

    function namesOf(ids) {
        return LIST_FORMAT.format(ids.map(nameOf));
    }

    // Only ever asked about a seat that is still at the table.
    function enemyIdsOf(id) {
        return enemiesOf(playerById(id)).map((enemy) => enemy.id);
    }

    // The seats of a step that are still at the table: one who left is neither waited for
    // nor painted.
    function presentSeats(ids) {
        return ids.filter((id) => playerById(id));
    }

    // This phone's answer: shown at once, then merged on the server so a phone answering
    // at the same moment cannot overwrite it. A device with no server keeps playing on
    // its own copy.
    async function submitAnswer(seatId, answer) {
        const step = state.minigame.id;
        state.minigame.answers = {...state.minigame.answers, [seatId]: answer};
        emitChange();
        const merged = await storage.answerRemote(state.code, step, seatId, answer);
        if (merged && merged.code === state?.code) {
            state = normalizeGameState(merged);
            storage.saveLocal(state);
        } else if (!storage.canSync()) {
            save();
        }
        refresh();
    }

    // The seat this phone plays in the step that is running, or null.
    function myMinigameSeat(kind, phase) {
        const me = myPlayer();
        const minigame = state?.minigame;
        if (!me || isDead(me) || minigame?.kind !== kind || minigame.phase !== phase || state.status !== "playing") return null;
        return minigame.seats.includes(me.id) ? me : null;
    }

    async function minigameHit() {
        const me = myMinigameSeat("blackjack", "play");
        if (!me || blackjackHand(state.minigame, me.id).status !== "playing") return;
        const hits = (state.minigame.answers[me.id]?.hits || 0) + 1;
        const next = blackjackHand({...state.minigame, answers: {...state.minigame.answers, [me.id]: {hits}}}, me.id);
        // Bust or 21 ends the hand by itself, so it goes in as standing.
        await submitAnswer(me.id, {hits, stand: next.status !== "playing"});
    }

    async function minigameStand() {
        const me = myMinigameSeat("blackjack", "play");
        if (!me || blackjackHand(state.minigame, me.id).status !== "playing") return;
        await submitAnswer(me.id, {hits: state.minigame.answers[me.id]?.hits || 0, stand: true});
    }

    // Anyone at the table may be voted out — except yourself, and in a 2 mot 2 your partner.
    async function minigameVote(targetId) {
        const me = myMinigameSeat("vote", "play");
        if (!me || !enemyIdsOf(me.id).includes(targetId)) return;
        await submitAnswer(me.id, {vote: targetId});
    }

    async function minigameChoose(targetId) {
        const me = myMinigameSeat("blackjack", "choose");
        if (!me || state.minigame.winnerId !== me.id || !enemyIdsOf(me.id).includes(targetId)) return;
        await submitAnswer(me.id, {target: targetId});
    }

    // The panel's player buttons: a vote on the ballot, the winner's pick in Blackjack.
    async function minigamePick(targetId) {
        if (state?.minigame?.kind === "vote") await minigameVote(targetId);
        else await minigameChoose(targetId);
    }

    // Only a result can be put away: a game in play is the whole screen until it is done.
    function dismissMinigamePanel() {
        if (state?.minigame?.phase !== "done") return;
        dismissedMinigameId = state.minigame.id;
        emitChange();
    }

    // Who settles a step: the starter's own phone, at once; the host only after the step's
    // time and a grace — unless the starter is a bot, whose phone is the host.
    function minigameRole(minigame) {
        const starter = playerById(minigame.starterId);
        const human = starter && !starter.isBot;
        if (human && starter.id === playerId) return "primary";
        if (!isHostDevice()) return null;
        return human ? "backup" : "primary";
    }

    // The bots' part, played by the host and nowhere else, like their turns.
    function driveMinigameBots(minigame) {
        if (!isHostDevice() || minigameElapsed() < Math.min(botDelay, 2500)) return;
        const waiting = minigame.phase === "choose" ? [minigame.winnerId] : minigame.seats.filter((id) => !minigame.answers[id]);
        waiting.map(playerById).filter((bot) => bot?.isBot && !isDead(bot)).forEach((bot) => {
            const key = `${minigame.id}:${bot.id}`;
            if (botAnswered.has(key)) return;
            botAnswered.add(key);
            submitAnswer(bot.id, botAnswer(minigame, bot));
        });
    }

    function botAnswer(minigame, bot) {
        if (minigame.phase === "choose") return {target: botTarget(enemiesOf(bot))};
        if (minigame.kind === "vote") return {vote: botTarget(enemiesOf(bot))};
        return {hits: botHits(stackOf(minigame, bot.id)), stand: true};
    }

    async function tickMinigame() {
        const minigame = state?.minigame;
        if (!minigameOpen() || state.status !== "playing" || minigameBusy) return;
        driveMinigameBots(minigame);
        const role = minigameRole(minigame);
        if (!role) return;
        const due = minigameElapsed() >= stepDuration(minigame) + (role === "backup" ? HOST_GRACE_MS : 0);
        if (!due && !(role === "primary" && stepComplete(minigame, livingIds()))) return;
        const step = minigame.id;
        minigameBusy = true;
        await pollOnce();
        minigameBusy = false;
        if (state?.minigame?.id !== step || state.status !== "playing") return;
        settleMinigameStep();
        save();
        refresh();
        if (maybeEndGame()) return;
        // The payout can knock out the player whose turn it is, and the death screen
        // covers Avsluta tur: the turn moves on by itself, as after a card.
        if (isDead(currentPlayer())) advanceTurn();
    }

    function settleMinigameStep() {
        const minigame = state.minigame;
        if (minigame.kind === "vote") settleVote(minigame);
        else if (minigame.phase === "play") settleBlackjackRound(minigame);
        else settleBlackjackChoice(minigame);
    }

    // The best hand under 22 wins. A tie plays again among the tied, a table of busts
    // plays again whole, and at the last round the tied draw lots.
    function settleBlackjackRound(minigame) {
        const living = livingIds();
        const seats = minigame.seats.filter((id) => living.includes(id));
        const hands = Object.fromEntries(seats.map((id) => [id, blackjackHand(minigame, id).cards]));
        const {winners, best} = blackjackLeaders(hands);
        const contenders = winners.length ? winners : seats;
        if (contenders.length > 1 && minigame.round < MAX_ROUNDS) {
            state.minigame = {...minigame, id: makeId(), round: minigame.round + 1, tie: best, seats: contenders, stacks: dealStacks(contenders, shuffle), answers: {}};
            state.log = winners.length ? `Lika på ${best} mellan ${namesOf(contenders)} — ny runda!` : "Alla blev tjocka — ny runda!";
            return;
        }
        const winnerId = contenders[contenders.length > 1 ? randomInt(0, contenders.length - 1) : 0];
        state.minigame = {...minigame, id: makeId(), phase: "choose", hands, winnerId, answers: {}};
        state.log = `${nameOf(winnerId)} vinner Blackjack${best ? ` med ${best}` : ""}! ${nameOf(winnerId)} väljer vem som dricker ${BLACKJACK_PRIZE}.`;
    }

    // The winner's pick, or — when the winner let the time run out — the worst hand.
    function settleBlackjackChoice(minigame) {
        const winner = playerById(minigame.winnerId);
        const targets = winner && !isDead(winner) ? enemiesOf(winner) : [];
        if (!targets.length) return closeMinigame(minigame, "Blackjack: ingen att dela ut till.");
        const chosen = targets.find((player) => player.id === minigame.answers[winner.id]?.target);
        const target = chosen || playerById(worstHand(targets.map((player) => player.id), minigame.hands));
        payOut(minigame, winner, target, BLACKJACK_PRIZE, `${winner.name} vann Blackjack och delar ut ${BLACKJACK_PRIZE} klunkar.`);
        state.log = `${winner.name} vann Blackjack${chosen ? "" : " (valde inte i tid)"} — ${target.name} dricker ${BLACKJACK_PRIZE}.`;
    }

    // Every vote a raffle ticket; the raffle's pick drinks. The draw is shown like a chance
    // card's, to everyone at the table, because everyone voted.
    function settleVote(minigame) {
        const living = livingIds();
        const starter = playerById(minigame.starterId);
        const valid = (voter, target) => minigame.seats.includes(voter) && living.includes(voter) && enemyIdsOf(voter).includes(target);
        const tally = voteTally(minigame, valid);
        if (!starter) return closeMinigame(minigame, "Utröstningen avbröts — den som startade den har gått.");
        if (!tally.length) return closeMinigame(minigame, "Ingen röstade — ingen dricker.");
        const tickets = voteTickets(tally);
        const loser = playerById(tickets[randomInt(0, tickets.length - 1)]);
        const gamble = {
            kind: "roulette",
            everyone: true,
            caption: `${tally.map(({id, votes}) => `${nameOf(id)} ${votes} röst${votes === 1 ? "" : "er"}`).join(" · ")} …`,
            headline: `${loser.name.toUpperCase()}!`,
            detail: `${loser.name} tar ${sipsText(VOTE_PRIZE)}`,
            tone: "win",
            candidateIds: tickets,
            pickIds: [loser.id],
        };
        payOut(minigame, starter, loser, VOTE_PRIZE, "Rösterna är räknade — varje röst blev en lott.", gamble, tally);
        state.log = `Utröstningen: ${loser.name} åker ut och dricker ${VOTE_PRIZE}.`;
    }

    // The payout is a card play of its own — the card sliding from the winner to the one
    // who drinks, sips held until it lands — so the TV shows it like any other.
    function payOut(minigame, from, target, amount, text, gamble = null, tally = []) {
        // A raffle's card names nobody: it hangs over the bar while the spotlight is still
        // choosing, and its foot would read the result out.
        const play = recordCardEvent(from, cardById(minigame.kind === "vote" ? "utrostningen" : "blackjack"), gamble ? null : target, text);
        if (gamble) play.gamble = gamble;
        // A lost game is a debt, not an attack: every screen has already said who drinks
        // how much, so it goes past guards and umbrellas, still credited to the winner.
        addSips(target, amount, from);
        drainNarration();
        state.minigame = {...minigame, id: makeId(), phase: "done", result: {loserId: target.id, amount, tally}};
        state.turnStamp = makeId();
    }

    function closeMinigame(minigame, log) {
        state.minigame = {...minigame, id: makeId(), phase: "done", result: null};
        state.turnStamp = makeId();
        state.log = log;
    }

    // The step's countdown, urgent for its last seconds like the turn clock.
    function minigameClock(minigame) {
        const seconds = secondsLeft(stepDuration(minigame), minigameElapsed());
        return {seconds, urgent: seconds <= TURN_URGENT_SECONDS};
    }

    // Why a round is being played again, and between whom.
    function replayNote(minigame, seats) {
        const why = minigame.tie ? `Lika på ${minigame.tie}` : "Alla blev tjocka";
        return `${why} — omspel mellan ${namesOf(seats)}!`;
    }

    function blackjackRow(minigame, id) {
        const player = playerById(id);
        const hand = blackjackHand(minigame, id);
        return {
            id,
            name: nameOf(id),
            icon: iconSymbol(player?.icon),
            color: colorHex(player?.color),
            cards: hand.cards.map(cardFace),
            total: hand.total,
            status: hand.status,
            statusText: blackjackStatusText(hand),
            winner: minigame.winnerId === id,
        };
    }

    // A phone's mini-game panel: its own hand and the others', the winner's choice, or the
    // ballot. Null when nothing is running — and once a vote is settled, because the
    // raffle's reveal takes over.
    function minigamePanel() {
        const me = myPlayer();
        const minigame = state?.minigame;
        // A player who is out has no seat in it, and the death screen to look at.
        if (!me || isDead(me) || !minigame || state.status !== "playing") return null;
        const base = {kind: minigame.kind, title: MINIGAME_TITLES[minigame.kind], phase: minigame.phase, round: minigame.round};
        if (minigame.phase === "done") {
            if (minigame.kind === "vote" || !minigame.result || minigameElapsed() >= DONE_MS || dismissedMinigameId === minigame.id) return null;
            const loserId = minigame.result.loserId;
            return {
                ...base,
                seconds: null,
                urgent: false,
                rows: presentSeats(Object.keys(minigame.hands)).map((id) => blackjackRow(minigame, id)),
                note: `${nameOf(minigame.winnerId)} vann · ${nameOf(loserId)} dricker ${minigame.result.amount}!`,
                personal: loserId === me.id ? `Du dricker ${minigame.result.amount}!` : null,
            };
        }
        const clock = minigameClock(minigame);
        if (minigame.kind === "vote") return {...base, ...clock, ...votePanel(minigame, me)};
        return {...base, ...clock, ...blackjackPanel(minigame, me)};
    }

    // The buttons a phone picks a player with — by face as well as by name, like the
    // target picker.
    function pickTargets(me, chosenId) {
        return enemyIdsOf(me.id).map((id) => ({
            id,
            name: nameOf(id),
            icon: iconSymbol(playerById(id).icon),
            color: colorHex(playerById(id).color),
            chosen: chosenId === id,
        }));
    }

    function votePanel(minigame, me) {
        const mine = minigame.answers[me.id]?.vote || null;
        const voted = minigame.seats.filter((id) => minigame.answers[id]?.vote).length;
        return {
            voted: Boolean(mine),
            prompt: mine ? "Rösten är lagd — tryck på någon annan för att byta." : "Vem ska åka ut? Varje röst blir en lott.",
            targets: pickTargets(me, mine),
            note: `${voted} av ${minigame.seats.length} har röstat.`,
        };
    }

    function blackjackPanel(minigame, me) {
        if (minigame.phase === "choose") {
            const mine = minigame.winnerId === me.id;
            const chosen = minigame.answers[me.id]?.target;
            return {
                hand: null,
                canHit: false,
                canStand: false,
                rows: presentSeats(Object.keys(minigame.hands)).map((id) => blackjackRow(minigame, id)),
                roundNote: null,
                prompt: mine ? `Du vann! Vem ska dricka ${BLACKJACK_PRIZE}?` : null,
                note: mine ? "Väljer du inte i tid går de till sämsta handen." : `${nameOf(minigame.winnerId)} vann och väljer vem som dricker ${BLACKJACK_PRIZE} …`,
                targets: mine ? pickTargets(me, chosen) : [],
            };
        }
        const seated = minigame.seats.includes(me.id);
        const hand = seated ? blackjackRow(minigame, me.id) : null;
        const playing = hand?.status === "playing";
        return {
            hand,
            canHit: playing,
            canStand: playing,
            rows: presentSeats(minigame.seats).filter((id) => id !== me.id).map((id) => blackjackRow(minigame, id)),
            roundNote: minigame.round > 1 ? replayNote(minigame, minigame.seats) : null,
            prompt: null,
            note: !seated ? "Du är ute ur den här rundan." : playing ? "Ta kort eller stanna." : "Väntar på de andra …",
            targets: [],
        };
    }

    // The TV's game table: every hand, or who has voted (never for whom), and the count-
    // down. Gone once a step is settled for good: the payout's card show has the bar.
    function minigameTable() {
        const minigame = state?.minigame;
        if (!minigameOpen() || state.status !== "playing") return null;
        const base = {kind: minigame.kind, title: MINIGAME_TITLES[minigame.kind], phase: minigame.phase, round: minigame.round, ...minigameClock(minigame)};
        const seats = presentSeats(minigame.hands ? Object.keys(minigame.hands) : minigame.seats);
        if (minigame.kind === "vote") {
            const voted = seats.filter((id) => minigame.answers[id]?.vote);
            return {
                ...base,
                note: `Alla röstar på telefonen — ${voted.length} av ${seats.length} har röstat.`,
                // Shaped like a hand's row, with no cards, so the table paints one kind of row.
                rows: seats.map((id) => ({
                    id,
                    name: nameOf(id),
                    icon: iconSymbol(playerById(id).icon),
                    color: colorHex(playerById(id).color),
                    cards: [],
                    status: voted.includes(id) ? "voted" : "voting",
                    statusText: voted.includes(id) ? "Har röstat" : "Röstar …",
                    winner: false,
                })),
            };
        }
        if (minigame.phase === "choose") {
            const best = blackjackHand(minigame, minigame.winnerId).total;
            // A winner by lot — the last round's ties, or a table of busts — has no count to brag about.
            const how = best <= 21 ? `med ${best}` : "på lottning";
            return {
                ...base,
                note: `${nameOf(minigame.winnerId)} vann ${how}! ${nameOf(minigame.winnerId)} väljer vem som dricker ${BLACKJACK_PRIZE} …`,
                rows: seats.map((id) => blackjackRow(minigame, id)),
            };
        }
        return {
            ...base,
            note: minigame.round > 1 ? `${replayNote(minigame, seats)} Närmast 21 vinner.` : "Alla spelar på telefonen — närmast 21 vinner.",
            rows: seats.map((id) => blackjackRow(minigame, id)),
        };
    }

    // A dawdler is counted for the end screen's Sölkorven; a player with nothing left
    // to play simply was done.
    function timeOutTurn(stuck) {
        const current = currentPlayer();
        if (!stuck) addStat(current, "skipped");
        advanceTurn({
            note: stuck ? `${current.name} hade inget mer att spela.` : `${current.name} tog för lång tid — turen gick vidare.`,
        });
    }

    function endTurn() {
        if (state?.status !== "playing" || minigameOpen()) return;
        // Players end their own turn; the host screen can force one through when a
        // phone has gone away and would otherwise stall the match.
        if (!isMyTurn() && !(isHostDevice() && !myPlayer())) return;
        advanceTurn();
    }

    // Bots are driven by the host screen and nowhere else. Two devices running the same
    // bot turn would POST two different states for one action, which is the one thing
    // the newest-wins sync model cannot survive.
    function isBotTurn() {
        return Boolean(state?.status === "playing" && isHostDevice() && currentPlayer()?.isBot);
    }

    function scheduleBotTurn() {
        // refresh() runs on every poll, so an unguarded reschedule would keep pushing
        // the bot's move further away and the match would never move.
        if (botTimer || !isBotTurn()) return;
        botTimer = scheduleTimeout(runBotStep, botDelay);
    }

    function runBotStep() {
        botTimer = null;
        if (!isBotTurn()) return;
        const self = currentPlayer();
        // The protection filter lives here rather than in the policy, so the policy
        // stays a pure read of the board it is given.
        const move = chooseBotMove({self, enemies: attackableEnemies(state, enemiesOf(self)), cardById, adjustedCost});
        const card = move ? cardById(move.cardId) : null;
        const handIndex = card ? self.hand.indexOf(card.id) : -1;
        // Nothing worth playing: end the turn rather than ask the same question again
        // on the next tick and never advance.
        if (!card) {
            advanceTurn();
            return;
        }
        // chooseBotMove only proposes moves playCard accepts (both check the same
        // adjustedCost); the refusal path survives only as drift insurance, so a
        // future policy/library mismatch ends the turn instead of looping forever.
        const landed = playCard(self, card, targetOf(move), handIndex);
        /* c8 ignore next */
        if (!landed) return advanceTurn();
        burstForCard(card, state.players.findIndex((player) => player.id === move.targetId), landed);
        save();
        refresh();
        if (maybeEndGame()) return;
        // A bot pricked out by a cactus must not keep playing its hand from the grave.
        if (isDead(self)) return advanceTurn();
        scheduleBotTurn();
    }

    function targetOf(move) {
        return state.players.find((player) => player.id === move.targetId) || null;
    }

    function botName() {
        const taken = new Set(state.players.map((player) => player.name));
        /* c8 ignore next 3 -- six names for a six-seat roster: while a bot can still be
           added at most five names are taken, so the fallback is pure drift insurance. */
        const free = BOT_NAMES.find((name) => !taken.has(name));
        return free || `Bot ${state.players.length + 1}`;
    }

    async function addBot() {
        // The old UI only hid the button; now that this is a public API the ?bots=1
        // switch is enforced here too.
        if (!canAddBot() || botRosterFull()) return;
        const bot = Player.create({
            name: botName(),
            id: makeId(),
            deck: buildDeck(),
            isBot: true,
            team: nextTeam(state.players),
        });
        const log = `${bot.name} kopplade upp sig.`;
        // Same merge as a phone joining: the host adding a bot must not overwrite a
        // player who pressed Gå med at the same moment.
        const merged = await storage.joinRemote(state.code, bot, log);
        if (merged && merged.code === state.code) {
            state = normalizeGameState(merged);
            storage.saveLocal(state);
        } else {
            state.players.push(bot);
            state.log = log;
            save();
        }
        refresh();
    }

    function maybeEndGame() {
        return isTeamGame() ? maybeEndTeamGame() : maybeEndSoloGame();
    }

    function maybeEndSoloGame() {
        const {ended, winner} = matchOutcome(state, isDead);
        if (!ended) return false;
        state.status = "ended";
        state.winnerId = winner.id;
        state.log = `${winner.name} vinner med ${winner.sipsTaken}/${winner.maxSips} klunkar.`;
        save();
        refresh();
        return true;
    }

    // A side wins together, so the celebration is keyed on the team rather than on one
    // seat. winnerId still names a member, because every other reader of the blob —
    // the stats rows included — understands a player id and not a team.
    function maybeEndTeamGame() {
        const {ended, team, members} = teamMatchOutcome(state, isDead);
        if (!ended) return false;
        state.status = "ended";
        state.winnerTeam = team;
        // winnerId still names one member, because every other reader of the blob —
        // the stats rows included — understands a player id and not a team.
        state.winnerId = members[0].id;
        state.log = `${teamLabel(team)} vinner: ${members.map((player) => player.name).join(" och ")}.`;
        save();
        refresh();
        return true;
    }

    // The board paints these; the controller only decides where they land, so the
    // rule (all enemies, self, or the picked target) stays under test.
    function burstForCard(card, targetIndex, landed) {
        // Damage bursts where it actually landed — Shotroulette's unlucky one, Skål!'s
        // own player, never the enemy whose umbrella caught it. A hit that landed
        // nowhere falls back to where it was aimed.
        if (card.effect === "damage" && landed.length) {
            landed.forEach((id) => emitBurst(state.players.findIndex((player) => player.id === id), card));
            return;
        }
        if (card.target === "allEnemies") {
            enemiesOf(currentPlayer()).forEach((enemy) => emitBurst(state.players.indexOf(enemy), card));
            return;
        }
        if (card.target === "self") {
            emitBurst(state.players.indexOf(currentPlayer()), card);
            return;
        }
        emitBurst(targetIndex, card);
    }

    function emitBurst(playerIndex, card) {
        emitEffect({kind: "burst", playerIndex, effect: card.effect, image: card.image});
    }

    function leaveGame() {
        if (state?.status === "lobby" && isHostDevice()) {
            closeLobbyFromHost();
            return;
        }
        const me = myPlayer();
        if (state?.status === "lobby" && me) {
            playerManager.without(me.id);
            state.log = `${me.name} lämnade lobbyn.`;
            save();
        } else if (state?.status === "playing" && me) {
            // Leaving mid-match has to take the seat out of the rotation too, or the
            // turn lands on a phone that is no longer there and nothing can advance it.
            const {players, active} = withoutPlayer(state, me.id);
            state.players = players;
            state.active = active;
            state.log = `${me.name} lämnade spelet.`;
            if (state.players.length < 2) {
                state.status = "ended";
                state.log = `${me.name} lämnade spelet. Matchen är slut.`;
            }
            save();
        }
        // refresh() re-reads the local copy when the URL carried a code, which would
        // put the player straight back into the match they just left. Dropping this
        // device's copy is what makes leaving stick — and it is what leaving means.
        if (state?.code) storage.removeLocal(state.code);
        storage.forgetSession();
        lobbyStep = urlGameCode ? "join" : "start";
        leaveArmed = false;
        state = null;
        replaceUrl(pathname);
        refresh();
    }

    function canKickPlayers() {
        return Boolean((state?.status === "lobby" || state?.status === "playing") && isHostDevice());
    }

    // A seat's kick button: armed after the first tap, with the copy that says so.
    // In a phone game the host is also a player, and nobody kicks themselves out of
    // their own lobby: that seat gets no button, and kickPlayer refuses it as well.
    function kickButton(player) {
        if (player.id === playerId) return null;
        const armed = kickPendingId === player.id;
        return {armed, label: armed ? "Säker?" : "Sparka"};
    }

    // The host screen removes a seat — a phone that died, a duplicate, someone who went
    // home. A whole-state save like a player leaving, so in the lobby it can race a join
    // the same way leaving can. The kicked phone sees its seat gone on its next poll.
    function kickPlayer(id) {
        if (!canKickPlayers()) return;
        const target = playerById(id);
        if (!target || id === playerId) return;
        if (kickPendingId !== id) {
            kickPendingId = id;
            emitChange();
            return;
        }
        kickPendingId = null;
        // A kicked phone may be holding the turn. Move it on first so whoever is next
        // gets a proper turn start, then take the seat out of the rotation.
        if (state.status === "playing" && currentPlayer().id === id) advanceTurn({outgoingActs: false});
        const {players, active} = withoutPlayer(state, id);
        state.players = players;
        state.active = active;
        state.kickedIds = [...state.kickedIds, id];
        state.log = `${target.name} togs bort av värden.`;
        save();
        refresh();
        if (state.status === "playing") maybeEndGame();
    }

    async function copyGameLink() {
        try {
            await clipboard.writeText(shareLink());
            state.log = "Länken kopierades.";
        } catch {
            state.log = "Kopiera länken från raden ovan.";
        }
        refresh();
    }

    async function pollOnce() {
        if (!state || !storage.canSync()) return;
        const remote = await storage.fetchRemote(state.code);
        if (!remote || remote.code !== state.code) return;
        if (remote.status === "closed") {
            storage.removeLocal(remote.code);
            exitToLobby("Lobbyn stängdes av skärmen.");
            return;
        }
        if ((remote.updatedAt || 0) > (state.updatedAt || 0)) {
            const seated = Boolean(myPlayer());
            state = remote;
            storage.saveLocal(state);
            normalizeGameState(state);
            // The seat vanished under this phone. The host kicked it — or, in a lobby, a
            // whole-state save raced its join. Either way there is nothing left to paint
            // here. Mid-match a seat only goes by a kick, since nobody can join; anything
            // else would be a forged state, and that is left to the usual normalizing.
            const kicked = state.kickedIds.includes(playerId);
            if (seated && !myPlayer() && (kicked || state.status === "lobby")) {
                exitToLobby(kicked ? "Värden tog bort dig från spelet." : "Din plats i lobbyn försvann. Gå med igen med koden.");
                return;
            }
            refresh();
        }
    }

    // Someone who drifts off the site comes back to the bare URL: a bookmark, a home
    // screen icon, a tab iOS quietly discarded. The code is gone from the address bar
    // by then and nobody memorises five random letters, so the device reopens the
    // lobby it remembers rather than showing a join form for a game already in play.
    async function resumeSession() {
        const remembered = storage.loadSession();
        const code = normalizeCode(remembered?.code || "");
        const resumed = code && now() - (remembered.at || 0) <= SESSION_MEMORY_MS
            ? await loadGame(code)
            : null;
        // Only into a lobby that is still running and still holds this device. A seat
        // that is gone means someone left, or the code now belongs to another party —
        // either way rejoining is the player's call to make, not ours.
        const seated = Boolean(resumed) && resumed.status !== "closed"
            && (resumed.hostId === playerId || resumed.players.some((player) => player.id === playerId));
        if (seated) {
            state = resumed;
            storage.saveLocal(state);
            pushResolvedUrl(code);
        } else if (remembered) {
            storage.forgetSession();
        }
        refresh();
    }

    // What startApp() did after wiring the DOM: sign-in state, the sync poll, and
    // routing the URL the page was opened with.
    function boot() {
        scheduleInterval(pollOnce, POLL_INTERVAL);
        scheduleInterval(tickTurnClock, TURN_TICK);
        scheduleInterval(tickMinigame, TURN_TICK);

        // A sign-in that came back rejected leaves ?auth=failed on the URL. Say so
        // once, then let the player carry on as a guest.
        if (qs.get("auth") === "failed") lobbyMessage = "Inloggningen misslyckades. Du kan spela som gäst ändå.";

        const accountKnown = refreshAccount();

        if (urlGameCode && view === "hand") return joinOnceAccountKnown(accountKnown);
        if (urlGameCode) return openGame(urlGameCode);
        return resumeSession();
    }

    // The lobby's "Återanslut" for a signed-in player: the newest lobby or match the
    // server still finds them seated in, from this device or any other.
    function reconnectOffer() {
        const seat = account.liveGame;
        if (!seat) return null;
        return {
            code: seat.code,
            label: `Återanslut till ${seat.code}`,
            detail: seat.status === "lobby" ? "Din plats i lobbyn väntar." : "Matchen pågår — din plats väntar på dig.",
        };
    }

    // This device already is that seat: just join. Another device's seat: take its
    // player id over for good and reload into the game, so the whole controller boots
    // as that player — exactly as if it had joined from here. The id is only ever one
    // the server found linked to this account, and ids are public in the state anyway.
    async function reconnect() {
        const seat = account.liveGame;
        if (!seat) return undefined;
        if (seat.playerId === playerId) return joinGame(seat.code);
        storage.setPlayerId(seat.playerId);
        redirect(`${pathname}?game=${seat.code}&view=hand`);
        return undefined;
    }

    // A phone opening a share link joins straight away — but a signed-in player's name
    // and deck ride on the account, and /auth/me may not have answered yet. Give it a
    // moment, never more: sign-in must not hold up the party.
    async function joinOnceAccountKnown(accountKnown) {
        await Promise.race([accountKnown, new Promise((resolve) => scheduleTimeout(resolve, ACCOUNT_WAIT_MS))]);
        return joinGame(urlGameCode);
    }

    // Derived answers the components would otherwise compute themselves, kept here so
    // the logic stays under unit test instead of inside the untested React shell.

    // The board's status line. It lived in the component, which is how it carried on
    // announcing "Anns tur · 4/6 mana" under a winner screen: nobody was up and no
    // mana was going to be spent.
    function boardStatus() {
        if (!state) return {headline: "", detail: ""};
        const celebration = winnerCelebration();
        if (celebration) {
            return {headline: "Matchen är slut", detail: `${celebration.name} vann`};
        }
        const current = currentPlayer();
        return {
            headline: `${current?.name || "Ingen"}s tur`,
            detail: `Runda ${numberText(state.round)} · ${numberText(current?.mana)}/${numberText(current?.maxMana)} mana`,
        };
    }

    // What was played, newest first, already turned into the lines a screen paints.
    // The board has painted this for ever; shaping it here is what lets the phone show
    // the same thing without a second copy of the formatting living in JSX.
    function matchHistory() {
        return buildBoardHistory(state?.events).map((entry) => ({
            id: entry.id,
            title: entry.playerName ? `${entry.playerName} spelade ${entry.cardName}` : entry.cardName,
            detail: entry.cardText || "",
            target: entry.targetName ? `Mot ${entry.targetName}` : "",
            drinks: entry.drinks.map(formatDrinkLine),
        }));
    }

    // The lobby's two swatch rows, shaped here so the components stay bindings: each
    // row is every option this build ships plus which one this device is playing under.
    function appearanceChoices() {
        return {
            icons: PLAYER_ICONS.map((icon) => ({...icon, selected: icon.id === chosenIcon})),
            colors: PLAYER_COLORS.map((color) => ({...color, selected: color.id === chosenColor})),
        };
    }

    // Everything the board needs to lay a roster out without seats landing on top of
    // each other: the ring, how far the cards have to shrink, and how tall the arena
    // has to be for that many of them. boardLayout.js owns the geometry and its test
    // proves the boxes stay apart; this only binds it to the current roster.
    // arena is the board's measured arena ({width, height}); until it has been measured
    // the seats keep the size the overlap proof was written for.
    function boardMetrics(arena) {
        const players = state?.players || [];
        const count = players.length;
        const meIndex = players.findIndex((player) => player.id === playerId);
        return {
            // Past the ring's limit the seats are laid out as a grid instead; the
            // coordinates below go unused there, and the board only reads the class.
            layout: boardLayoutFor(count),
            scale: Math.floor(seatScale(count) * seatFit({count, ...arena}) * 1000) / 1000,
            height: arenaHeight(count),
            seats: players.map((player, index) => ({
                ...seatPosition({index, count, meIndex}),
                icon: iconSymbol(player.icon),
                color: colorHex(player.color),
            })),
        };
    }

    // The link a player scans or pastes. Built from the injected origin/pathname for
    // the same reason pushUrl is: it is the only way a test can see what gets copied.
    function shareLink() {
        return `${origin}${pathname}?game=${state.code}&view=hand`;
    }

    // What the TV lobby tells the room to open. Nobody clicks a link on a TV: they read
    // the address off the screen and type the code, so the scheme and query are noise.
    function joinHost() {
        return origin.replace(/^[a-z]+:\/\//i, "");
    }

    // The two statistics surfaces, shaped here rather than in JSX so the copy and the
    // coercion stay under test: the lobby's history panel and the host's top five.
    function accountGames() {
        return recentGameRows(account);
    }

    // The lobby's account block: the sign-in pitch, or who you are signed in as. Its
    // `name` is also what tells the lobby to drop the name field — a signed-in player
    // plays under the account name rather than typing one per device.
    function accountBlock() {
        return {...accountPanel(account), deckStatus: deckStatusText()};
    }

    function deckStatusText() {
        if (!account.authenticated) return "";
        if (!account.deck) return "Ingen egen lek — du spelar standardleken.";
        if (!isValidDeck(account.deck.cards, BUILDABLE_IDS)) return "Din sparade lek gäller inte längre.";
        return `Egen lek sparad · starthand ${fitOpeningHand(account.deck.openingHand, account.deck.cards).length}/4`;
    }

    // Null while the builder is closed — or once its player has signed out.
    function deckBuilder() {
        if (!deckDraft || !account.authenticated) return null;
        return deckBuilderView({
            draft: deckDraft,
            saved: account.deck,
            library: BUILDABLE_CARDS,
            status: deckSaveStatus,
            tab: deckTab,
            onlyInDeck: deckOnlyInDeck,
            closeArmed: deckCloseArmed,
        });
    }

    // The waiting room's two switches. Only the host screen gets them: the board view
    // also mounts on spectators' screens, and only the host's choice is ever dealt.
    function settingsPanel() {
        const {deckMode, openingHand} = gameSettings;
        const option = (value, label, current) => ({value, label, active: value === current});
        return {
            visible: Boolean(state?.status === "lobby" && isHostDevice()),
            deckModes: [option("own", "Egna lekar", deckMode), option("shared", "Samma för alla", deckMode)],
            handModes: [option("random", "Slumpad", openingHand), option("fixed", "Egen", openingHand)],
            handDisabled: deckMode === "shared",
            note: deckMode === "shared"
                ? "Alla spelar standardleken."
                : openingHand === "fixed"
                    ? "Inloggade med egen lek börjar med sina valda startkort. Övriga får standardleken."
                    : "Inloggade med egen lek spelar den, med slumpad starthand. Övriga får standardleken.",
        };
    }

    // The line under each name in the waiting room, so the table can see who brought
    // a deck. Checked like the dealer checks it, so a forged list reads as no deck.
    function seatStatus(player) {
        return isValidDeck(player.customDeck, BUILDABLE_IDS) ? "Redo · egen lek" : "Redo";
    }

    function topPlayers() {
        return leaderboardRows(leaderboard);
    }

    function selectedCard() {
        const me = myPlayer();
        return (me && cardById(me.hand[selectedCardIndex])) || null;
    }

    // Null unless the target picker should be open, otherwise everything it needs.
    function targetSelection() {
        const card = targeting ? selectedCard() : null;
        return card && needsTarget(card) ? {card, enemies: attackableEnemies(state, enemiesOf(myPlayer()))} : null;
    }

    // Notes the local moment each fresh chance play first reached this device. A play
    // that arrives long after it happened (a reload, a phone back from sleep) is history.
    function observeGambles() {
        (state?.events || []).forEach((event) => {
            if (event?.type !== "card" || !event.gamble || gambleSeen.has(event.id)) return;
            if (now() - event.at < GAMBLE_FRESH_MS) gambleSeen.set(event.id, now());
        });
    }

    // Who a phone reveal is for. A phone game has no TV, so every phone is the stage.
    // At a party the TV is, and the reveal goes to whoever is in it: the thrower, the
    // target, everyone the roulette might pick, and anyone it actually hit.
    function inGamble(play, gamble, me) {
        if (gamble.everyone || isPhoneGame() || play.playerId === me.id || play.targetId === me.id) return true;
        if ((gamble.kind === "roulette" || gamble.kind === "bottle") && gamble.candidateIds.includes(me.id)) return true;
        return playConsequences(state.events, play.id).some((event) => event.playerId === me.id);
    }

    // The newest chance play this phone is part of, with its gamble, while it still has
    // something to show.
    function currentGamble() {
        const me = myPlayer();
        if (!me) return null;
        const play = [...state.events].reverse().find((event) => gambleSeen.has(event?.id));
        const gamble = play && normalizeGamble(play.gamble);
        if (!gamble || !inGamble(play, gamble, me)) return null;
        return {play, gamble, me, elapsed: now() - gambleSeen.get(play.id)};
    }

    // The phone's moment of suspense: the coin in the air, the tumbling die, the names the
    // roulette runs over, the wheel — and, once it lands, the result and what it means
    // for this player. Null when there is nothing to reveal or it has been tapped away.
    function gambleReveal() {
        const current = currentGamble();
        if (!current || current.play.id === dismissedRevealId) return null;
        const {play, gamble, me, elapsed} = current;
        const phase = gamblePhase(elapsed);
        if (phase === "done") return null;
        const landed = phase === "result";
        return {
            id: play.id,
            phase,
            kind: gamble.kind,
            cardName: String(play.cardName || "Kort"),
            playerName: String(play.playerName || ""),
            caption: gamble.caption,
            headline: landed ? gamble.headline : null,
            detail: landed ? gamble.detail : null,
            tone: gamble.tone,
            personal: landed ? personalLine(play, me) : null,
            ...gambleProp(gamble, elapsed, landed),
        };
    }

    // What the reveal shows for its kind. The coin and the wheel turn by CSS to the angle
    // given here; the die's face and the roulette's spotlight are answered frame by frame.
    function gambleProp(gamble, elapsed, landed) {
        if (gamble.kind === "coin") return {coin: {face: gamble.face, ...coinFrame(elapsed, gamble.face)}};
        if (gamble.kind === "dice") {
            const face = landed ? gamble.value : diceFace(elapsed, gamble.sides, gamble.value);
            return {die: {face, pips: dicePips(face)}};
        }
        if (gamble.kind === "duel") {
            return {
                duel: duelFaces(elapsed, gamble.values).map((die, side) => ({
                    name: gamble.names[side],
                    face: die.face,
                    rolling: die.rolling,
                    pips: dicePips(die.face),
                    lost: landed && gamble.loser === side,
                })),
            };
        }
        if (gamble.kind === "bottle") {
            // A phone lays the table out as a ring of names; the bottle turns to the one
            // the roll picked. A forged empty ring still gets a bottle to spin.
            const count = gamble.candidateIds.length || 1;
            const landing = Math.max(0, gamble.candidateIds.indexOf(gamble.pickIds[0]));
            return {
                bottle: {
                    angle: bottleSpin(elapsed, bottleRing(landing, count)),
                    names: gamble.candidateIds.map((id, index) => ({
                        id,
                        name: playerById(id)?.name || "?",
                        at: (index * 360) / count,
                        picked: landed && gamble.pickIds.includes(id),
                    })),
                },
            };
        }
        if (gamble.kind === "wheel") {
            const angle = wheelFrame(elapsed, gamble.index, gamble.segments.length);
            return {wheel: {segments: gamble.segments, angle, pointer: wheelPointer(angle, gamble.segments.length)}};
        }
        const landings = gamble.pickIds.map((id) => gamble.candidateIds.indexOf(id)).filter((index) => index >= 0);
        const frame = rouletteFrame(elapsed, gamble.candidateIds.length, landings);
        return {
            roulette: gamble.candidateIds.map((id, index) => ({
                id,
                name: playerById(id)?.name || "?",
                spot: frame.spot === index,
                picked: frame.locked.includes(index),
            })),
        };
    }

    // "Drick 6!" for whoever it hit, "Du klarade dig!" for whoever it could have.
    function personalLine(play, me) {
        const mine = playConsequences(state.events, play.id)
            .filter((event) => (event.type === "drink" || event.type === "death") && event.playerId === me.id);
        const amount = mine.reduce((total, event) => total + (Number(event.amount) || 0), 0);
        if (amount) return `Drick ${amount}${mine.some((event) => event.type === "death") ? " + en shot" : ""}!`;
        return play.playerId === me.id ? null : "Du klarade dig!";
    }

    function dismissGambleReveal() {
        const reveal = gambleReveal();
        if (!reveal || reveal.phase !== "result") return;
        dismissedRevealId = reveal.id;
        emitChange();
    }

    // The drinks a chance play caused on this phone were announced by its reveal, so the
    // drink alert leaves them out rather than shouting the result over the coin.
    function revealedDrinkIds() {
        const current = currentGamble();
        return new Set(current ? playConsequences(state.events, current.play.id).map((event) => event.id) : []);
    }

    // The newest recent play that moved this player's cards, whether or not the notice
    // has been tapped away — the hand keeps marking the new arrivals either way.
    function latestCardNotice() {
        return cardNoticeFor(state?.events, myPlayer()?.id, now(), CARD_NOTICE_MS);
    }

    // The phone's "which cards?" answer: what the last play took, gave or drew for you.
    function cardNotice() {
        const notice = latestCardNotice();
        return notice && notice.id !== dismissedNoticeId ? notice : null;
    }

    function dismissCardNotice() {
        const notice = cardNotice();
        if (!notice) return;
        dismissedNoticeId = notice.id;
        emitChange();
    }

    // One row per card in hand, with the cost, the affordability rule and the button
    // copy already applied — the phone maps over these and paints.
    function handCards() {
        const me = myPlayer();
        if (!me) return [];
        const myTurn = isMyTurn();
        const fresh = freshHandFlags(me.hand, latestCardNotice()?.gained);
        const waiting = minigameOpen();
        return me.hand.flatMap((cardId, index) => {
            const card = cardById(cardId);
            // An id this bundle does not know — a card from a newer deploy, or a forged
            // hand — is skipped rather than allowed to blank the phone.
            if (!card) return [];
            const cost = adjustedCost(me, card);
            const blocked = cost > me.mana;
            return [{
                card,
                cost,
                index,
                blocked,
                playable: myTurn && !blocked && !waiting,
                selected: index === selectedCardIndex,
                // Just drawn, taken or handed over: the phone marks it "Ny".
                fresh: fresh[index],
                detail: damageDetail(card),
                cta: waiting
                    ? "Minispel pågår"
                    : blocked
                    ? `Saknar mana (${cost})`
                    : myTurn
                        ? `Spela för ${cost} mana`
                        : "Inte din tur",
            }];
        });
    }

    // " · 3 klunkar" on the expanded card, read from the bot's power table. Cards whose
    // damage depends on the board say nothing rather than a number that is wrong.
    function damageDetail(card) {
        const power = CARD_POWER[card.id];
        if (!power.damage || power.variable) return "";
        return ` · ${sipsText(power.damage)}`;
    }

    // The board's fireworks screen and the winner's phone both bind this: null until
    // the match has ended with a winner still seated to celebrate.
    function winnerCelebration() {
        if (state?.status !== "ended") return null;
        if (isTeamGame()) {
            const team = normalizeTeam(state.winnerTeam);
            const members = membersOfTeam(team);
            return {
                team: true,
                name: teamLabel(team),
                members: members.map((player) => player.name),
                isMe: members.some((player) => player.id === playerId),
            };
        }
        if (!state.winnerId) return null;
        const winner = state.players.find((player) => player.id === state.winnerId);
        return winner ? {team: false, name: winner.name, members: [winner.name], isMe: winner.id === playerId} : null;
    }

    // This phone's end screen: how the match went for them, their own numbers, and the
    // awards with theirs marked. Null mid-match, and on a device without a seat.
    function matchSummary() {
        const me = myPlayer();
        if (state?.status !== "ended" || !me) return null;
        const celebration = winnerCelebration();
        const team = isTeamGame();
        const won = Boolean(celebration?.isMe);
        return {
            won,
            headline: summaryHeadline(won, team, me),
            detail: summaryDetail(won, celebration),
            rows: statRows(me.stats).map((row) => ({...row, value: numberText(row.value)})),
            awards: matchAwards(state.players).map((award) => ({...award, mine: award.playerIds.includes(me.id)})),
        };
    }

    function summaryHeadline(won, team, me) {
        if (team) return won ? "NI VANN!" : "Ni förlorade";
        return won ? "DU VANN!" : `${ordinal(placementOf(me, state.players))} plats`;
    }

    function summaryDetail(won, celebration) {
        if (!celebration) return "Matchen är slut.";
        if (!won) return `${celebration.name} vann matchen.`;
        // A 2 mot 2 is won together, so it names the side and both of its players.
        return celebration.team
            ? `${celebration.name}: ${celebration.members.join(" och ")}.`
            : `Grattis ${celebration.name} — färst klunkar i hela laget!`;
    }

    // The board's award mats under the winner: who drank most and least, healed and
    // guarded most, and the rest of the evening's honours.
    function boardAwards() {
        return state?.status === "ended" ? matchAwards(state.players, BOARD_AWARDS) : [];
    }

    function membersOfTeam(team) {
        return state.players.filter((player) => normalizeTeam(player.team) === normalizeTeam(team));
    }

    // The two sides as the 2 mot 2 lobby paints them, so the waiting room can show who
    // is on which side before anyone commits to starting.
    function teamRoster() {
        if (!isTeamGame()) return [];
        return TEAM_LABELS.map((label, team) => {
            const members = membersOfTeam(team);
            return {
                team,
                label,
                names: members.map((player) => player.name),
                seats: members.map((player) => ({
                    id: player.id,
                    name: player.name,
                    icon: iconSymbol(player.icon),
                    color: colorHex(player.color),
                    mine: player.id === playerId,
                })),
                // Empty chairs are what tells the room how many phones are still missing.
                open: Math.max(0, TEAM_ROSTER / TEAM_LABELS.length - members.length),
            };
        });
    }

    // With no TV, the phone has to be the board too. One row per seat — your side
    // first, then theirs — carrying everything the strip paints, already shaped and
    // coerced so the component stays a binding.
    function tableSeats() {
        // Any phone mode, not just the 2 mot 2: a duel has no TV either, so the
        // opponent's klunkar have to be readable somewhere.
        const me = isPhoneGame() ? myPlayer() : null;
        if (!me) return [];
        const players = state.players;
        const mates = [me, ...teammatesOf(players, me)];
        const theirs = opponentsOf(players, me);
        const current = currentPlayer();
        // "Ditt lag" only means something when there is one; in a duel the top row is
        // simply you.
        const ownSideLabel = isTeamGame() ? "Ditt lag" : "Du";
        return [...mates, ...theirs].map((player, index) => ({
            id: player.id,
            // One heading per side, on its first row: without it four similar cards
            // read as a list rather than as two teams facing each other.
            heading: index === 0 ? ownSideLabel : (index === mates.length ? "Motståndare" : null),
            name: player.name,
            icon: iconSymbol(player.icon),
            color: colorHex(player.color),
            sips: `${numberText(player.sipsTaken)}/${numberText(player.maxSips)}`,
            // What the folded row shows: the count alone, because at chip size the
            // maxSips half is the same for everyone and only costs legibility.
            chip: numberText(player.sipsTaken),
            danger: Math.max(0, Math.round((player.sipsTaken / player.maxSips) * 100)),
            mine: player.id === playerId,
            ally: normalizeTeam(player.team) === normalizeTeam(me?.team),
            active: state?.status === "playing" && player.id === current?.id,
            out: isDead(player),
            minions: player.minions.map((minion) => ({
                id: minion.id,
                name: minion.name,
                hp: `${numberText(minion.hp)}/${numberText(minion.maxHp)}`,
            })),
        }));
    }

    function turnKicker() {
        if (state?.status === "playing") return isMyTurn() ? "Din tur" : "Spelar nu";
        return state?.status === "lobby" ? "Lobby" : "Matchen är slut";
    }

    // The figure beside the header's name, so it is always the figure of whoever is named.
    // It used to be this phone's own, which put your fox next to "Bob" on Bob's turn and
    // read as if the phone were his.
    function turnFigure() {
        const seat = state?.status === "playing" ? currentPlayer() : myPlayer();
        return seat ? {icon: iconSymbol(seat.icon), color: colorHex(seat.color)} : null;
    }

    // A seat in a party lobby. There is nothing to play yet, so rather than the match
    // screen — 0/0 mana and an empty hand saying to end your turn — the phone says it is in.
    function phoneWaiting() {
        if (state?.status !== "lobby" || !myPlayer()) return null;
        return {status: waitingStatusText(), headcount: `${state.players.length} spelare inne`};
    }

    // The phone's "Du blev träffad" overlay: the newest recent hit on this seat, unless
    // the player has tapped it away. It covers the whole phone, and a hit landing just
    // before your turn held the hand hostage for the full five seconds.
    function drinkAlert() {
        const me = myPlayer();
        if (!me || isDead(me)) return null;
        const revealed = revealedDrinkIds();
        const recent = recentDrinkEvents(state.events, now()).filter((event) => !revealed.has(event.id));
        const event = personalDrinkEvent(recent, me.id);
        if (!event || event.id === dismissedAlertId) return null;
        const fatigue = event.cause === "fatigue";
        return {
            id: event.id,
            amount: numberText(event.amount),
            title: fatigue ? "Slut på kort!" : "Du blev träffad",
            source: drinkAlertSource(event, me.id),
            // The empty deck bites one sip harder every turn; saying so is the warning.
            next: fatigue ? `Nästa tur utan kort: ${sipsText(me.fatigue + 1)}.` : null,
        };
    }

    // The phone header's deck chip: how many cards are left, or — once there are none —
    // the warning that the next turn start costs a fatigue sip more than the last.
    function deckChip() {
        const me = myPlayer();
        if (!me) return null;
        if (me.deck.length) return {label: `${me.deck.length} kort kvar`, warning: false};
        return {label: `Tom lek · nästa tur ${sipsText(me.fatigue + 1)}`, warning: true};
    }

    function dismissDrinkAlert() {
        const alert = drinkAlert();
        if (!alert) return;
        dismissedAlertId = alert.id;
        emitChange();
    }

    // Mid-match the phone's header names whoever is up; otherwise it names its owner.
    function turnHeadline() {
        if (state?.status === "playing") return currentPlayer()?.name || "Ingen";
        return myPlayer()?.name || "";
    }

    // The footer line under Avsluta tur: what this player can still do right now.
    function turnFootHint() {
        if (state?.status !== "playing") {
            return state?.status === "lobby" ? "Väntar på att spelet startar." : state?.log || "";
        }
        if (!isMyTurn()) return `${currentPlayer()?.name || "Någon annan"}s tur — vänta på din.`;
        const me = myPlayer();
        // Free cards (Isvattenpaus, Dricks, Kreditkortet) stay playable at zero mana.
        if (canPlayAnyCard(me, cardById, adjustedCost)) {
            return me.mana > 0 ? `Du kan spela kort för ${me.mana} mana till.` : "Du kan fortfarande spela kort som kostar 0.";
        }
        return me.mana === 0 ? "Ingen mana kvar — avsluta turen." : "Inga spelbara kort — avsluta turen.";
    }

    // Only the host is told it can start, because only the host's button works.
    function waitingStatusText() {
        const seats = seatsFor(state?.mode);
        if (seats) {
            const missing = seats - state.players.length;
            if (missing > 0) return `Väntar på ${missing} telefon${missing === 1 ? "" : "er"} till.`;
            return isHostDevice() ? "Alla är inne. Kör!" : "Väntar på att den som skapade startar.";
        }
        if (!state?.players.length) return "Väntar på första telefonen.";
        return isHostDevice()
            ? "Starta spelet när alla i soffan syns här."
            : "Väntar på att värden startar spelet.";
    }

    // Leaving is offered from the phone as well as the board, and stays offered once
    // you are knocked out: a dead player would otherwise be pinned to the death screen
    // until the rest of the table finished.
    function canLeaveMatch() {
        return Boolean(state && myPlayer());
    }

    function canStartGame() {
        if (state?.status !== "lobby" || !isHostDevice()) return false;
        // A 2 mot 2 needs all four seats: starting a 2v1 would hand one side a free win.
        const seats = seatsFor(state.mode);
        // A fixed roster has to be full: starting a 2 mot 2 at three would hand one
        // side a free win, and a duel at one has nobody to duel.
        return seats ? state.players.length === seats : state.players.length >= 2;
    }

    function isTeamGame() {
        return isTeamMode(state);
    }

    // No TV: every device is a phone, the creator plays, and the roster is fixed.
    function isPhoneGame() {
        return isPhoneMode(state);
    }

    // normalizeMode has already guaranteed the id is one of ours, so this always hits.
    function modeLabel(mode) {
        return GAME_MODES.find((entry) => entry.id === normalizeMode(mode)).label;
    }

    // The lobby's mode picker: what this build can create, shaped for the buttons.
    function gameModeChoices() {
        return GAME_MODES.map((mode) => ({id: mode.id, label: mode.label, blurb: mode.blurb}));
    }

    function canAddBot() {
        return Boolean(botsEnabled && state?.status === "lobby" && isHostDevice());
    }

    function botRosterFull() {
        return Boolean(state && state.players.length >= (seatsFor(state.mode) || MAX_BOT_ROSTER));
    }

    function canEndTurn() {
        if (state?.status !== "playing" || minigameOpen()) return false;
        const me = myPlayer();
        return me ? currentPlayer()?.id === me.id : isHostDevice();
    }

    function endTurnLabel() {
        return !myPlayer() && isHostDevice() ? "Hoppa över tur" : "Avsluta tur";
    }

    // Whose seat the turn lands on next, for the header's "är näst" chip. Null when
    // there is no rotation to speak of — lobby, or the turn only comes back to the
    // same player.
    function nextPlayerName() {
        if (state?.status !== "playing" || state.players.length < 2) return null;
        const next = state.players[advanceActive(state, isDead).active];
        return next === currentPlayer() ? null : next.name;
    }

    snapshot = buildSnapshot();

    return {
        subscribe(listener) {
            listeners.add(listener);
            return () => listeners.delete(listener);
        },
        subscribeEffects(listener) {
            effectListeners.add(listener);
            return () => effectListeners.delete(listener);
        },
        getSnapshot: () => snapshot,
        soundBoard,
        boot,
        createGame,
        joinGame,
        openGame,
        startGame,
        selectCard,
        cancelSelection,
        closeTargetModal,
        requestPlaySelectedCard,
        playSelectedCard,
        endTurn,
        addBot,
        leaveGame,
        copyGameLink,
        startLogin,
        endLogin,
        accountGames,
        accountBlock,
        openDeckBuilder,
        closeDeckBuilder,
        addDeckCard: (id) => editDeckDraft((draft) => addCard(draft, id)),
        removeDeckCard: (id) => editDeckDraft((draft) => removeCard(draft, id)),
        toggleOpeningCard: (id) => editDeckDraft((draft) => togglePick(draft, id)),
        unpickOpeningCard: (index) => editDeckDraft((draft) => unpickAt(draft, index)),
        chooseDeckTab,
        toggleDeckFilter,
        resetDeckDraft: () => editDeckDraft(() => ({cards: standardDeck(), openingHand: []})),
        saveDeck,
        deckBuilder,
        setDeckMode,
        setOpeningHandMode,
        settingsPanel,
        seatStatus,
        reconnectOffer,
        reconnect,
        canKickPlayers,
        kickButton,
        kickPlayer,
        appearanceChoices,
        boardMetrics,
        isTeamGame,
        isPhoneGame,
        gameModeChoices,
        goToCreate,
        goToJoin,
        goBack,
        matchHistory,
        boardStatus,
        toggleHistory,
        requestLeave,
        leaveLabel,
        teamRoster,
        tableSeats,
        toggleTable,
        canLeaveMatch,
        chooseIcon,
        chooseColor,
        topPlayers,
        closeLobbyOnPageExit,
        pollOnce,
        cardById,
        adjustedCost,
        needsTarget,
        myPlayer,
        currentPlayer,
        isHostDevice,
        isMyTurn,
        enemiesOf,
        shareLink,
        joinHost,
        selectedCard,
        targetSelection,
        handCards,
        winnerCelebration,
        matchSummary,
        boardAwards,
        turnKicker,
        turnHeadline,
        turnFigure,
        phoneWaiting,
        drinkAlert,
        dismissDrinkAlert,
        deckChip,
        cardNotice,
        dismissCardNotice,
        gambleReveal,
        dismissGambleReveal,
        minigamePanel,
        minigameTable,
        minigameHit,
        minigameStand,
        minigameVote,
        minigameChoose,
        minigamePick,
        dismissMinigamePanel,
        turnFootHint,
        waitingStatusText,
        canStartGame,
        canAddBot,
        botRosterFull,
        canEndTurn,
        endTurnLabel,
        nextPlayerName,
        turnClock,
    };
}
